import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Tooltip, Marker, useMap, useMapEvent, GeoJSON, ZoomControl } from 'react-leaflet';
import { Box, IconButton, Typography, Paper, Button, Slide, Fade, TextField, InputBase, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Divider, Select, FormControl, InputLabel, Chip } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import customPin from './assets/LoRaSiPin.png';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SensorsIcon from '@mui/icons-material/Sensors';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AirIcon from '@mui/icons-material/Air';
import BoltIcon from '@mui/icons-material/Bolt';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import GrassIcon from '@mui/icons-material/Grass';
import DatabasePage from "./pages/DatabasePage";
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { generateFakePins, generateFakeSensorData, generateFakeHealthMap } from './fakeData';
import { formatLastSeen, formatExactTime } from './relativeTime';
import { isDuplicateSelection, findNode } from './nodeSensors';
import TowerDriftChart from "./components/TowerDriftChart";
import TemperatureChart from "./components/TemperatureChart";
import PIREventLog from "./components/PIREventLog";
import WeatherNodeTabs from "./components/WeatherNodeTabs";
import FireDetectionsLayer, { formatDetectionAge } from "./components/FireDetectionsLayer";
import SegmentNdviLayer, { useSegmentNdvi } from "./components/SegmentNdviLayer";
import PowerMeterPanel from "./components/PowerMeterPanel";
import PowerMeterChart from "./components/PowerMeterChart";
import Sidebar from "./components/Sidebar";
import HelpLauncher from "./components/HelpLauncher";
import { APP_VERSION } from "./appVersion";
import AnomaliesPanel from "./components/AnomaliesPanel";
import NodeSatelliteSnapshot from "./components/NodeSatelliteSnapshot";
import NodeGroundStability from "./components/NodeGroundStability";
import NodeLineIdentity from "./components/NodeLineIdentity";
import { nearestPowerLine } from "./powerLineIdentity";
import WeatherAlertDetail from "./components/WeatherAlertDetail";
// The one help affordance on this page (#171). Every explanation it renders is
// a key in helpTopics.js, not a sentence written at the call site — see that
// file for why a `title=` attribute was never enough here.
import HelpTip from "./components/HelpTip";
import { groupAlerts } from "./weatherAlerts";
import { useLanguage } from "./LanguageContext";
// Every /api/ call below goes through this rather than `fetch` directly: the
// backend verifies the Cognito ID token on each request since #129, so a call
// without the Authorization header it attaches is a 401. The non-/api/ fetches
// (power_lines.geojson, Nominatim) stay on plain `fetch` — sending this token
// to a third party would be worse than pointless.
import { apiFetch } from "./apiFetch";

Amplify.configure({
  API: {
    GraphQL: {
      endpoint: 'https://vpc5e4c7urgebjbymeq63wsllm.appsync-api.eu-north-1.amazonaws.com/graphql',
      region: 'eu-north-1',
      defaultAuthMode: 'apiKey',
      apiKey: 'da2-gsa35fi3lbfnrmoewgtiuhb36y'
    }
  }
});

const API_BASE = "https://samst.live";
const HEALTH_POLL_MS = 15 * 60 * 1000;
// While health is failing, retry far more often than the normal poll so a
// transient outage does not leave the map in an unknown state for 15 minutes.
const HEALTH_RETRY_MS = 60 * 1000;
const SENSOR_POLL_MS = 5 * 60 * 1000;
// Matches the lambda_weather EventBridge schedule — polling faster than the
// Lambda writes would only re-read the same snapshot.
const WEATHER_POLL_MS = 30 * 60 * 1000;
// Matches the lambda_fires EventBridge schedule (#93), for the same reason.
// Neither figure controls how fresh a detection can be: FIRMS publishes NRT
// active fire "within 3 hours of a satellite observation (on a best-effort
// basis)", so this poll only decides how long a published detection waits before
// it reaches the map, not how long it waits to exist.
const FIRES_POLL_MS = 15 * 60 * 1000;
// The satellite Lambda runs weekly (#103), so this poll cannot make a reading
// arrive sooner — it exists so a session left open across the Monday run picks
// the new one up, and so a run that fails becomes visible without a reload.
// Six hours rather than a week: the browser is not the schedule.
const SATELLITE_POLL_MS = 6 * 60 * 60 * 1000;

// Below this share of the bounding box surviving cloud masking, a reading is
// still shown but is labelled as a partial view. Sentinel-2 sees the corridor
// through whatever gap the cloud leaves, and a mean over a third of the area is
// not the same statement as a mean over all of it.
const NDVI_PARTIAL_VIEW_BELOW = 0.75;

const STATUS_COLOR = {
  ok:      "#22c55e",
  warning: "#f59e0b",
  offline: "#6b7280",
  alert:   "#ef4444",
  unknown: "#9ca3af",
};

// A node's pin shows the worst status among its sensors, so a single failing
// sensor is never hidden behind a healthy sibling in the same box.
// /api/health only ever answers ok | warning | offline | alert, so "unknown" is
// ours alone: it means we have no answer, and it is deliberately not on this
// scale.
const STATUS_SEVERITY = { ok: 0, warning: 1, offline: 2, alert: 3 };

// Names a sensor for the tooltip. The type is what an operator recognises; the
// device ID is what keeps two sensors of the same type on one node distinct.
const sensorName = (s) => s.sensor_type ? `${s.sensor_type} (${s.deviceId})` : s.deviceId;

// Payload channels whose name is a placeholder rather than a description. The
// device reported them and a decoder kept them, but nothing here knows what they
// measure, so the number carries no meaning for a reader.
//
// The ADW300 is why this exists: it interleaves a frame of 24 channels the TTN
// formatter has no names for (identifiers 45-68), which printed as 24 rows of
// "raw unknown 45 ... raw unknown 68" and buried the readings that do mean
// something. The S2120's `unknown_u16` and any `measurement_<id>` from a firmware
// change fall in the same class.
//
// This hides them from the reading list only. They are still stored in DynamoDB
// and still returned by the API, so nothing is lost for analysis — and when one
// is identified it gets a real name in that device's decoder and reappears here
// with no change to this file. Deliberately keyed on the placeholder naming
// convention rather than on a device, so a new sensor family inherits it.
//
// Note what is NOT hidden: `temp_n_c`, `di_status`, `rain_gauge` and the withheld
// per-phase values are unverified, but they are named — a reader can tell what
// they refer to and judge them. Only the anonymous ones go.
const isPlaceholderChannel = (key) =>
  /^(unknown($|_)|unmapped_|measurement_\d)/.test(key.replace(/^raw_/, ''));

// Worst health record among a node's sensors, with the status the whole node
// deserves and the sensors we have no answer for.
//
// A sensor missing from /api/health used to be dropped before the comparison,
// so a node with one healthy sensor and one missing record read "ok" and the
// missing sensor was invisible (#66). It now counts: alert wins — an unanswered
// sensor must not mask a sibling that is actively failing — otherwise a missing
// record forces "unknown", which can never be read as healthy.
//
// Returns null only when no sensor answered at all, leaving the caller's
// pin.status ?? "unknown" fallback (#33) to speak for the whole node.
function worstHealth(pin, healthMap) {
  const sensors = pin.sensors ?? [];
  const records = sensors.map(s => healthMap[s.deviceId]).filter(Boolean);
  if (records.length === 0) return null;
  const unknownSensors = sensors.filter(s => !healthMap[s.deviceId]);
  const worst = records.reduce((worst, h) =>
    (STATUS_SEVERITY[h.status] ?? 0) > (STATUS_SEVERITY[worst.status] ?? 0) ? h : worst
  );
  const status = worst.status === "alert" ? "alert"
    : unknownSensors.length > 0 ? "unknown"
    : worst.status;
  // Spread the worst answered record so its battery and last-seen still read in
  // the tooltip even when the node as a whole reports "unknown".
  return { ...worst, status, unknownSensors };
}

const STATUS_LABEL_KEY = {
  ok:      "statusOk",
  warning: "statusWarning",
  offline: "statusOffline",
  alert:   "statusAlert",
  unknown: "statusUnknown",
};

const statusLabel = (status, t) => t(STATUS_LABEL_KEY[status] ?? "statusUnknown");

// Cognito group that unlocks real data. Everyone else — signed in or not —
// sees generated data and never reaches /api/.
const SUPERUSER_GROUP = 'superusers';

// Sensor types a device can be registered as. Must stay in step with
// SENSOR_TYPES in DataIngestionESM/routes/sensors.mjs, which rejects anything
// else — these are the types the ingestion decoder produces and the only ones
// this dashboard has a panel for. 'unknown' is offered deliberately: an
// operator adding a device they cannot yet classify should not have to guess.
//
// That "must stay in step" was not holding. Ingestion has emitted 'weather' since
// #77 and this dashboard has had a weather panel since #80, but neither list
// offered the type, so the only way to register the live station was as
// 'unknown' — which hides every panel gated on the real type. #99 adds 'weather'
// alongside 'power' rather than leaving the same trap set for the next device.
const REGISTERABLE_SENSOR_TYPES = ['tilt', 'pir', 'temperature', 'weather', 'power', 'unknown'];

// The real deployment lives in the SamstNodes table and is loaded over
// /api/nodes, so a pin added on the map survives a refresh and is shared by
// every super user session. Only 'superuser' reads it; the other tiers get
// generateFakePins() instead and never call /api/.
// One pin = one physical end node. A node holds one or more sensors that live
// in the same box, each with its own device ID and type.
//
// Nodes are seeded once with DataIngestionESM/scripts/seed_nodes.mjs — that
// script now holds the three pilot nodes this file used to hardcode.

// Reads the Cognito group claim and the signed-in identity off the current
// session. Throws if there is no session, so callers can treat rejection as
// 'guest'. The account menu (#60) needs the name alongside the tier, and both
// come from the same round trip.
async function resolveAccount() {
  const user = await getCurrentUser();
  const session = await fetchAuthSession();
  const groups = session.tokens?.idToken?.payload['cognito:groups'] ?? [];
  return {
    tier: groups.includes(SUPERUSER_GROUP) ? 'superuser' : 'user',
    // loginId is what was typed to sign in. `username` is the Cognito sub on
    // pools configured with email sign-in, which would show the user a UUID.
    username: user.signInDetails?.loginId ?? user.username ?? null,
  };
}

// Rendered inside <Authenticator> once Cognito reports a signed-in user, so the
// account is re-read exactly once after login rather than on every render.
function AuthSessionSync({ onResolved }) {
  useEffect(() => {
    resolveAccount()
      .then(onResolved)
      .catch(() => onResolved({ tier: 'user', username: null }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Placeholder shown to guests where a chart or event log would be.
function LockedPanel({ message, onLogin, loginLabel }) {
  return (
    <Box sx={{
      mt: 3, p: 3, border: '1px solid #e5e7eb', borderRadius: 2,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 1, bgcolor: '#f9fafb',
    }}>
      <Typography sx={{ fontSize: 24 }}>🔒</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
        {message}
      </Typography>
      <Button variant="contained" size="small" onClick={onLogin}>
        {loginLabel}
      </Button>
    </Box>
  );
}

function makePinIcon(status) {
  const color = STATUS_COLOR[status] ?? STATUS_COLOR.unknown;
  // "unknown" means we have no health answer for this pin. Draw it as a hollow
  // ring so it can never be read as a solid green "ok" or a solid grey
  // "offline" — an unanswered pin must look different from an answered one.
  const dot = status === "unknown"
    ? `background:#fff;border:2px solid ${color};`
    : `background:${color};border:2px solid #fff;`;
  return L.divIcon({
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
    html: `
      <div style="position:relative;width:32px;height:32px;">
        <img src="${customPin}" style="width:32px;height:32px;" />
        <span style="
          position:absolute;top:0;right:0;
          width:11px;height:11px;
          ${dot}
          border-radius:50%;
          display:block;
        "></span>
      </div>`,
  });
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, map.getZoom());
  }, [position, map]);
  return null;
}

// Copernicus attribution for the Sentinel-2 NDVI reading (#103).
//
// ESA's terms require "Contains modified Copernicus Sentinel data" wherever
// Copernicus data is used, and #103 asked for it on the TileLayer attribution.
// It is NOT put there: the basemap is Esri World Imagery, and a Copernicus
// credit attached to that layer would state that the imagery on screen is
// Sentinel data. It is not, and on a map an operator uses to judge ground
// conditions that is exactly the kind of wrong attribution the licence exists
// to prevent.
//
// So it goes on Leaflet's attribution control — the map-wide credit list, which
// already carries Esri as a separate entry — and only while a Sentinel-derived
// reading is actually on screen. Added imperatively rather than through the
// TileLayer prop because react-leaflet fixes `attribution` at layer creation,
// and this one has to come and go with the tier and the data.
function CopernicusAttribution({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const credit = 'Contains modified Copernicus Sentinel data (ESA)';
    const control = map.attributionControl;
    if (!control) return undefined;
    control.addAttribution(credit);
    return () => control.removeAttribution(credit);
  }, [active, map]);
  return null;
}

const CentralMapClickHandler = ({ selectedLineId, setSelectedLineId, handleAddPin, setDashboardOpen, setSelectedPin, setSensorData, setSensorError }) => {
  useMapEvent('click', (e) => {
    if (selectedLineId !== null) { setSelectedLineId(null); return; }
    handleAddPin(e.latlng);
    setDashboardOpen(false);
    setSelectedPin(null);
    setSensorData(null);
    setSensorError(null);
  });
  return null;
};

// Deepest zoom the Esri World Imagery mosaic actually has imagery for over the
// pilot area, measured tile by tile (#82): z19 returns distinct real tiles, while
// z20 and z21 return one byte-identical 2,521-byte placeholder for every tile
// coordinate — a canned out-of-coverage response, not imagery. Leaflet's own
// default is 18, so before this the map was leaving a free zoom level unused:
// z18 is ~0.44 m/px at this latitude, z19 ~0.22 m/px. Those are TILE GRID
// scales, not the imagery's resolution: the source here resolves 0.5 m and
// Esri draws it only to map level 17, so z18 and z19 are magnified rather
// than sharper. Measured 2026-09-06 (#82) — SATELLITE_IMAGERY.md §1 has the
// command to re-check both figures.
//
// Capping here rather than letting Leaflet upscale is deliberate: the placeholder
// is served as HTTP 200 with a real image body, so nothing downstream can detect
// it and a zoom past 19 would silently show fake "imagery".
//
// Resolution is the limit, not the cap: a tower base is 5-12 m across, so at z19 a
// tower is 23-54 px. Enough for site context — access road, vegetation, a landslip
// or flooding at the foundation — and nowhere near enough for structural
// inspection. The imagery is also old; see the note on the TileLayer below.
const SATELLITE_MAX_ZOOM = 19;

// Track an element's rendered height, so chrome stacked below it can be
// positioned from what is actually on screen instead of an assumed constant.
//
// The mobile stack was originally laid out against fixed 40px steps, which holds
// only while every banner's text fits one line. On a narrow phone they wrap, the
// banner grows past its allowance, and the card below — drawn at a higher zIndex —
// covers its bottom edge.
//
// `freeze` holds the last measurement instead of re-measuring, for an element that
// can expand over what is below it rather than pushing it down. `watch` is the
// value whose change means the element mounted or unmounted, since a ref's
// identity never changes and the effect would otherwise not re-attach.
function useMeasuredHeight(ref, fallback, { freeze = false, watch } = {}) {
  const [height, setHeight] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || freeze) return undefined;
    // offsetHeight is 0 in jsdom, where nothing is laid out; keeping the previous
    // value means the test environment never sees a nonsense 0px offset.
    const measure = () => setHeight(h => el.offsetHeight || h);
    measure();
    // Catches wrap changes from rotation and from the text itself changing, neither
    // of which a resize listener alone would see. Absent in jsdom, so the one-shot
    // measure above is the fallback.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, freeze, watch]);
  return height;
}

// The same idea on the other axis, for chrome that has to stay clear of a
// neighbour sideways rather than stack under it. The width in question is never
// a constant: the language + account cluster changes size with the language
// ("Logout" vs "Odjava"), with the auth tier ("Login" vs "Logout") and with how
// wide the platform draws the flag glyph. Hard-coding it is how #104 happened.
function useMeasuredWidth(ref, fallback) {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // offsetWidth is 0 both in jsdom and while the element is display:none —
    // the cluster is hidden on mobile and behind an expanded panel. Keeping the
    // last value there means a hidden neighbour never reads as a zero-wide one
    // and so never invites the chip row to grow over the space it will reclaim.
    const measure = () => setWidth(w => el.offsetWidth || w);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// Acquisition date for the vegetation indicator, in the day-first en-GB form the
// charts and weather alerts already use, so the same instant reads identically
// everywhere in the dashboard.
//
// Returns null when there is no date. The caller substitutes an explicit
// "date unknown" rather than dropping it: an NDVI figure with no date beside it
// is read as today's, and it very often is not.
function formatSceneDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const isMobileDevice = window.matchMedia("(pointer: coarse)").matches;
const PANEL_WIDTH = isMobileDevice ? '100vw' : 480;
const PANEL_WIDTH_PX = isMobileDevice ? window.innerWidth : 480;
const PANEL_EXPANDED_WIDTH = 'calc(100vw - 80px)';

// Shared left edge for the desktop chrome that sits to the right of the search
// bar — the category chip row and the weather banner line up on it. It cannot be
// centred: the row is roughly 505px wide with full labels and the search bar
// reaches x=484 when expanded, so a centred row would overlap it on anything
// narrower than about 1480px. Both readers use this constant so they cannot
// drift apart.
const DESKTOP_CHROME_LEFT = 650;

// Right edge of the desktop language + account cluster, and the clear space kept
// between it and anything that scrolls up against it. The cluster reads this so
// the chip row's right bound cannot drift away from where the cluster actually
// sits (#104).
const DESKTOP_CONTROLS_RIGHT = 32;
const DESKTOP_CHROME_GAP = 16;

// Width of the fade drawn over a clipped end of the chip row. Wide enough to cut
// through a chip's fill rather than just its border, which is what makes it read
// as "there is more" instead of as a rendering artefact.
const CHIP_ROW_FADE = 28;

// Smallest the desktop chip row is allowed to get. Between the 768px mobile
// breakpoint and roughly 1040px the row's two ends cross — DESKTOP_CHROME_LEFT
// alone is past the cluster — and an absolutely positioned box with left and
// right over-constrained resolves to zero width, which would clip the row away
// to nothing. The floor makes it overflow there instead, which is what it did at
// every width before #104.
const CHIP_ROW_MIN_WIDTH = 200;

// Nominal size of every sensor-category glyph, desktop and mobile alike. One
// constant rather than five literals so the set cannot drift apart again the
// way it did in #207 — see the note above `categories`.
const CATEGORY_ICON = 18;

function HomePage() {
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sensorTypeFilter, setSensorTypeFilter] = useState(null);
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchSeqRef = useRef(0);
  const autoClickedRef = useRef(false);
  const sensorCacheRef = useRef({});
  const [mapCenter, setMapCenter] = useState([42.4413, 19.2629]);
  const [searchValue, setSearchValue] = useState('');
  const [lines, setLines] = useState(null);
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [selectedSensorIndex, setSelectedSensorIndex] = useState(0);
  const activeDeviceIdRef = useRef(null);
  const [sensorData, setSensorData] = useState(null);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [sensorError, setSensorError] = useState(null);
  const [availableSensors, setAvailableSensors] = useState([]);
  const [pendingLatLng, setPendingLatLng] = useState(null);
  const [addPinDialogOpen, setAddPinDialogOpen] = useState(false);
  // Node pending deletion, awaiting confirmation. Holds the node itself rather
  // than an id so the dialog can name it without looking it up again.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [addMode, setAddMode] = useState('new'); // 'new' | 'existing'
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [areaInput, setAreaInput] = useState('');
  // Register-a-device form, folded away inside the assign-sensor dialog. The
  // dropdown it sits under is populated from the sensor registry, which only
  // ingestion writes to — so an operator whose device has not reported yet sees
  // an empty list and, before this form existed, had nothing they could do.
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerDeviceId, setRegisterDeviceId] = useState('');
  const [registerType, setRegisterType] = useState('tilt');
  const [registerLabel, setRegisterLabel] = useState('');
  const [registerBusy, setRegisterBusy] = useState(false);
  // Message from the API, shown verbatim: it names the actual rule that was
  // broken ("already registered", the id format), which a generic "could not
  // register" would throw away.
  const [registerError, setRegisterError] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [healthMap, setHealthMap] = useState({});
  const [healthError, setHealthError] = useState(false);
  // null | 'load' = the node list could not be fetched | 'save' = a pin change
  // was not written back. Both are shown in the status banner: an unsaved edit
  // that stays on screen looks identical to a saved one until the next refresh.
  const [nodesError, setNodesError] = useState(null);
  const [healthConfigStatus, setHealthConfigStatus] = useState(null);
  const healthErrorRef = useRef(false);
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);
  // null = show all | 'tilt' | 'pir' | 'temperature'
  const [weather, setWeather] = useState(null);
  // The weather banner's expanded per-alert detail (#92). Hover drives it on
  // desktop, tap on mobile — one piece of state either way, so the two do not
  // have to agree about which is "open".
  const [weatherDetailOpen, setWeatherDetailOpen] = useState(false);
  // NASA FIRMS thermal-anomaly detections (#93). null = never fetched.
  const [fires, setFires] = useState(null);
  // The detections layer is OFF by default. It is regional context most of the
  // time, and a map that draws it unasked buries the sensor network it exists to
  // show. The near-node banner below does not depend on this flag: an actionable
  // detection must not be hidden behind a layer the operator forgot to enable.
  const [firesVisible, setFiresVisible] = useState(false);
  // Sentinel-2 NDVI over the corridor (#103). null = never fetched.
  const [satellite, setSatellite] = useState(null);
  // Per-segment corridor NDVI, drawn as a GeoServer WMS layer (#146). OFF by
  // default for the same reason as the fire layer: it is an overlay the operator
  // asks for, not part of the sensor network the map exists to show.
  const [segmentsVisible, setSegmentsVisible] = useState(false);

  // 'guest' = not signed in | 'user' = signed in, no superusers group |
  // 'superuser' = signed in and in the superusers group.
  const [authTier, setAuthTier] = useState('guest');
  const [authChecked, setAuthChecked] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [username, setUsername] = useState(null);
  // Anchor for the mobile account menu (#60). Null means closed.
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  // Everything below 'superuser' runs on generated data and issues no /api/ call.
  const isSuperuser = authTier === 'superuser';
  const simulated = !isSuperuser;

  const { t, language, setLanguage } = useLanguage();

  // Width breakpoint for the mobile layout. Held in state with a resize
  // listener so rotating a phone re-lays out the page without a reload — a
  // one-shot read on mount leaves landscape stuck with the portrait layout.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // The sensor panel covers the whole screen below the width breakpoint, and
  // also on a coarse-pointer device of any width — that second case predates
  // this breakpoint (see PANEL_WIDTH) and a tablet must keep it now that
  // isMobile is width-based rather than pointer-based.
  const panelFullScreen = isMobile || isMobileDevice;

  // All five are MUI glyphs, all at CATEGORY_ICON (#207). Three of them were
  // bitmaps until this change, which is why they looked bigger: a PNG fills its
  // box edge to edge, while a glyph is drawn inside a 24px viewBox with its own
  // padding. Measured at the nominal 18px, tilt.png covered the full 18x18 and
  // 78% of the box in ink — it was a filled disc — against 15.0x12.8 and 24%
  // for AirIcon beside it. That is the whole of the reported bug; the numbers
  // were never the problem.
  //
  // Do not give one of these its own fontSize to even them up by eye. Material
  // balances the set across the 24px viewBox already (a bolt is narrow, wind is
  // wide, and that is deliberate), and a per-icon override holds only until the
  // next glyph swap, with nothing recording why the values differ.
  //
  // Inheriting currentColor is the other reason to be rid of the bitmaps: the
  // chip below paints its contents white while its filter is active, which the
  // PNGs ignored — they stayed full-colour on the blue fill.
  //
  // `short` is used for the mobile chip row: the full labels do not fit three
  // across a phone viewport, and the icon already conveys "sensor".
  const categories = [
    { label: t('catTiltSensors'),   short: t('catTiltShort'),   icon: <SquareFootIcon        sx={{ fontSize: CATEGORY_ICON }} />, sensor_type: 'tilt' },
    { label: t('catMotionSensors'), short: t('catMotionShort'), icon: <DirectionsRunIcon     sx={{ fontSize: CATEGORY_ICON }} />, sensor_type: 'pir' },
    { label: t('catTempSensors'),   short: t('catTempShort'),   icon: <DeviceThermostatIcon  sx={{ fontSize: CATEGORY_ICON }} />, sensor_type: 'temperature' },
    { label: t('catWeatherStations'), short: t('catWeatherShort'), icon: <AirIcon  sx={{ fontSize: CATEGORY_ICON }} />, sensor_type: 'weather' },
    { label: t('catPowerMeters'),     short: t('catPowerShort'),   icon: <BoltIcon sx={{ fontSize: CATEGORY_ICON }} />, sensor_type: 'power' },
  ];

  // Starts empty: the tier effect below either loads the real nodes from
  // /api/nodes or swaps in generated pins, once the session is resolved.
  const [pins, setPins] = useState([]);

  // A node stays on the map as long as any of its sensors matches the filter.
  const filteredPins = activeTypeFilter
    ? pins.filter(p => (p.sensors ?? []).some(s => s.sensor_type === activeTypeFilter))
    : pins;

  // selectedPin is a snapshot taken at click time; resolve it against `pins`
  // so tabs and coordinates stay live after a drag or an added sensor.
  const selectedNode = selectedPin
    ? (pins.find(p => p.id === selectedPin.id) ?? selectedPin)
    : null;
  const activeSensor = selectedNode?.sensors?.[selectedSensorIndex];

  // Which mapped power line passes nearest the open node (#209). No new request:
  // power_lines.geojson is already in `lines` because the map draws it, and its
  // attributes were simply being discarded. The scan is 28,571 segments of plain
  // arithmetic, so it runs on selection rather than being precomputed for every
  // pin — keyed on the coordinates themselves so a dragged pin re-matches.
  //
  // The coordinates are pulled out rather than passed as `selectedNode.position`
  // so the dependency list is a pair of numbers. The array itself is rebuilt by
  // every /api/nodes poll, which would re-run the scan on identical input.
  const selectedLat = selectedNode?.position?.[0];
  const selectedLng = selectedNode?.position?.[1];
  const selectedLineMatch = useMemo(
    () =>
      lines && selectedLat != null && selectedLng != null
        ? nearestPowerLine([selectedLat, selectedLng], lines)
        : null,
    [lines, selectedLat, selectedLng]
  );

  // ── data fetching ──────────────────────────────────────────────────────────

  // An error body from /api/health is still valid JSON, so without the res.ok
  // check below a 503 parses fine, `data.devices` is undefined, and the map
  // silently ends up empty — which used to render every pin as green.
  const fetchHealth = () => {
    return apiFetch(`${API_BASE}/api/health`)
      .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
      .then(data => {
        const map = {};
        (data.devices ?? []).forEach(d => { map[d.device_id] = d; });
        setHealthMap(map);
        setHealthConfigStatus(data.config_status ?? null);
        healthErrorRef.current = false;
        setHealthError(false);
      })
      .catch(err => {
        console.error("Health fetch error:", err);
        // Drop the stale map rather than keep showing minutes-old statuses as
        // current: unknown-with-a-banner is honest, stale green is not.
        setHealthMap({});
        setHealthConfigStatus(null);
        healthErrorRef.current = true;
        setHealthError(true);
      });
  };

  // The map's nodes are stored in DynamoDB, not in this file, so a pin added
  // here is still here after a refresh and is visible to other super users.
  // Same res.ok guard as fetchHealth: an error body is valid JSON, so without
  // it a 500 parses, `data.nodes` is undefined, and the map silently renders
  // as an empty deployment.
  const fetchNodes = () => {
    if (!isSuperuser) return;
    return apiFetch(`${API_BASE}/api/nodes`)
      .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
      .then(data => {
        setPins((data.nodes ?? []).map(n => ({
          // The table's key doubles as the React key — node_id is unique by
          // definition, and pins loaded here have no other stable id.
          id: n.node_id,
          nodeId: n.node_id,
          label: n.label ?? n.node_id,
          position: n.position,
          area: n.area,
          sensors: n.sensors ?? [],
          // Carried through for the line panel (#209), which must say "nearest"
          // rather than "belongs" for a node the operator has ruled off the
          // line. Distance cannot stand in for this: node-1785520669405 is
          // flagged off-corridor and still sits 0 m from a mapped line.
          offCorridorReason: n.off_corridor_reason ?? null,
        })));
        setNodesError(null);
      })
      .catch(err => {
        console.error("Nodes fetch error:", err);
        // "No nodes deployed" and "the node list failed to load" draw the
        // exact same empty map, so the banner has to say which one this is.
        setNodesError('load');
      });
  };

  // Every node write goes through here. A silent failure would put a pin on
  // the map that was never stored — which is the bug this is meant to fix,
  // only now invisible until the next refresh — so failures raise the banner.
  const persistNode = (method, path = '', body) => {
    if (!isSuperuser) return;
    return apiFetch(`${API_BASE}/api/nodes${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); })
      .catch(err => {
        console.error(`Node ${method} error:`, err);
        setNodesError('save');
      });
  };

  const fetchSensorData = (deviceId, silent = false) => {
    if (simulated) {
      if (!silent) setSensorLoading(true);
      // Sensors live on the node, so look the type up across every node's
      // sensors rather than on the pin itself.
      const sensor = pins
        .flatMap(p => p.sensors ?? [])
        .find(s => s.deviceId === deviceId);
      const fake = generateFakeSensorData(deviceId, sensor?.sensor_type ?? 'tilt');
      sensorCacheRef.current[deviceId] = fake;
      // Brief delay so the panel shows its loading state rather than snapping —
      // the background poll applies immediately.
      setTimeout(() => {
        // Same guard as the real branch: a poll that lands after the user has
        // moved to another sensor tab must not overwrite the visible panel.
        if (activeDeviceIdRef.current === deviceId) {
          setSensorData(fake);
          setSensorLoading(false);
        }
      }, silent ? 0 : 600);
      return;
    }
    if (!silent) setSensorLoading(true);
    return apiFetch(`${API_BASE}/api/sensors/${deviceId}`)
      .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
      .then(data => {
        sensorCacheRef.current[deviceId] = data;
        // Only paint the panel if this response is for the sensor tab the
        // user is currently looking at.
        if (activeDeviceIdRef.current === deviceId) {
          setSensorData(data);
          setSensorLoading(false);
        }
      })
      .catch(err => {
        console.error("Error fetching sensor data:", err);
        if (!silent) { setSensorError(t("failedToLoadSensor")); setSensorLoading(false); }
      });
  };

  // ── auth tier ──────────────────────────────────────────────────────────────

  // Resolve the account once on mount. No session at all → guest.
  useEffect(() => {
    resolveAccount()
      .then(({ tier, username: name }) => { setAuthTier(tier); setUsername(name); })
      .catch(() => { setAuthTier('guest'); setUsername(null); })
      .finally(() => setAuthChecked(true));
  }, []);

  // Swap the map between the real deployment and a generated one whenever the
  // tier settles or changes. The cached readings and the auto-open flag belong
  // to the outgoing pin set, so both are reset here.
  useEffect(() => {
    if (!authChecked) return;
    sensorCacheRef.current = {};
    autoClickedRef.current = false;
    setSelectedPin(null);
    setSensorData(null);
    setSensorError(null);
    setDashboardOpen(false);
    setNodesError(null);
    // Superuser pins come from the SamstNodes table now, so clear first rather
    // than leaving the outgoing tier's pins on screen while the real ones are
    // still in flight. Lower tiers stay on generated nodes deliberately —
    // reading real node positions out of the database would defeat the
    // fake-data layer.
    //
    // The generated array is kept in `nextPins` because the health block below
    // needs it: healthMap is derived from those same nodes.
    let nextPins = [];
    if (isSuperuser) {
      setPins([]);
      fetchNodes();
    } else {
      nextPins = generateFakePins();
      setPins(nextPins);
    }
    // Health comes from /api/, which only superusers call. Clearing the error
    // flag stops a warning from the previous tier outliving it. The map itself
    // is filled from the generated nodes rather than left empty, because the
    // Sidebar derives its sensor list from healthMap and would otherwise show
    // a permanent loading state with no error to explain it.
    if (!isSuperuser) {
      setHealthMap(generateFakeHealthMap(nextPins));
      setHealthConfigStatus(null);
      healthErrorRef.current = false;
      setHealthError(false);
    }
  }, [authTier, authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── data fetching, gated on tier ───────────────────────────────────────────

  // Self-scheduling poll (rather than setInterval) so the delay can shrink to
  // HEALTH_RETRY_MS after a failure and return to HEALTH_POLL_MS on recovery,
  // with only ever one request in flight. Only superusers reach /api/, so for
  // every other tier this never starts and healthMap stays empty.
  useEffect(() => {
    if (!authChecked || !isSuperuser) return;
    let cancelled = false;
    let timer = null;
    const poll = () => {
      fetchHealth().then(() => {
        if (cancelled) return;
        timer = setTimeout(poll, healthErrorRef.current ? HEALTH_RETRY_MS : HEALTH_POLL_MS);
      });
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [authChecked, isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchActive && searchInputRef.current) searchInputRef.current.focus();
  }, [searchActive]);

  // Only superusers can attach a real sensor to a pin, so only they need the
  // registry — and only they may call /api/. Pulled out of the effect below so
  // registering a device can refresh the list without waiting for a tier change.
  const fetchAvailableSensors = () => {
    if (!isSuperuser) return;
    return apiFetch(`${API_BASE}/api/sensors`)
      .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
      .then(data => setAvailableSensors(Array.isArray(data) ? data : []))
      .catch(err => console.error("Error fetching sensors:", err));
  };

  useEffect(() => {
    if (!authChecked || !isSuperuser) { setAvailableSensors([]); return; }
    fetchAvailableSensors();
  }, [authChecked, isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weather drives the banner only. Gated on isSuperuser like every other
  // /api/ call — tiers below superuser must issue no network request at all,
  // so for them `weather` stays null and the banner never renders.
  useEffect(() => {
    if (!authChecked || !isSuperuser) { setWeather(null); return; }
    const fetchWeather = () => {
      apiFetch(`${API_BASE}/api/weather`)
        .then(res => res.json())
        .then(data => setWeather(data))
        .catch(err => console.error('Weather fetch error:', err));
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, WEATHER_POLL_MS);
    return () => clearInterval(interval);
  }, [authChecked, isSuperuser]);

  // FIRMS fire detections (#93). Same tier gate as weather: below superuser no
  // request is issued and `fires` stays null, so neither the layer toggle nor the
  // banner renders. There is deliberately no generated fallback — a fabricated
  // fire drawn at real coordinates on a real map is a different order of
  // misleading from a generated tilt reading in a panel.
  //
  // A failed fetch sets an explicit unavailable object rather than leaving the
  // last good response in place. Keeping stale detections on screen would show
  // an hours-old fire as current; showing none silently would read as "no
  // fires". Neither is acceptable, so the state says "unknown" outright (#57).
  useEffect(() => {
    if (!authChecked || !isSuperuser) { setFires(null); return; }
    const fetchFires = () => {
      apiFetch(`${API_BASE}/api/fires`)
        .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
        .then(data => setFires(data))
        .catch(err => {
          console.error('Fires fetch error:', err);
          // edge_clipped null, matching the route's own degraded shape: nothing
          // was polled, so no boundary was measured against either (#100).
          setFires({ status: 'unavailable', reason: null, detections: [], detection_count: 0, near_node_count: null, edge_clipped: null });
        });
    };
    fetchFires();
    const interval = setInterval(fetchFires, FIRES_POLL_MS);
    return () => clearInterval(interval);
  }, [authChecked, isSuperuser]);

  // Sentinel-2 vegetation status (#103). Same tier gate as weather and fires:
  // below superuser no request is issued and `satellite` stays null, so the
  // indicator never renders. There is deliberately no generated fallback — a
  // fabricated NDVI is a claim about real ground the operator can walk to.
  //
  // A failed fetch sets an explicit unavailable object rather than leaving the
  // last response in place, for the same reason as fires: a week-old "vegetation
  // normal" left on screen is a statement about today that nobody made (#57).
  useEffect(() => {
    if (!authChecked || !isSuperuser) { setSatellite(null); return; }
    const fetchSatellite = () => {
      apiFetch(`${API_BASE}/api/satellite`)
        .then(res => { if (!res.ok) throw new Error(`HTTP error: ${res.status}`); return res.json(); })
        .then(data => setSatellite(data))
        .catch(err => {
          console.error('Satellite fetch error:', err);
          setSatellite({ status: 'unavailable', reason: null, stress_level: null, ndvi_mean: null });
        });
    };
    fetchSatellite();
    const interval = setInterval(fetchSatellite, SATELLITE_POLL_MS);
    return () => clearInterval(interval);
  }, [authChecked, isSuperuser]);

  // Per-segment corridor NDVI (#146). The numbers behind the WMS tiles, read
  // from GeoServer's WFS so the summary can be shown in both languages.
  //
  // Gated on isSuperuser exactly like every /api/ consumer above, even though
  // the OGC endpoints are unauthenticated by necessity — a Leaflet tile request
  // cannot carry an Authorization header, so nginx has to leave /ows and /wfs
  // open and this gate is the only thing keeping real corridor measurements off
  // a map that is otherwise showing generated data for lower tiers.
  //
  // Fetched whether or not the layer is toggled on, so the chip can state what
  // is there before it is switched on — the same reason the fire chip carries a
  // count while its layer is off.
  const segmentNdvi = useSegmentNdvi({ baseUrl: API_BASE, enabled: authChecked && isSuperuser });

  // node_id → the operator-facing label, so the summary can name a segment by
  // the two towers it runs between. PostGIS knows the pair (segment_id is
  // "node-002__node-003") but not what an operator calls them; the node list
  // has the labels and nothing else joins the two.
  const nodeLabelsById = useMemo(
    () => Object.fromEntries(pins.map(p => [p.nodeId, p.label])),
    [pins],
  );

  useEffect(() => {
    if (!authChecked) return;
    if (pins.length > 0 && !autoClickedRef.current) {
      autoClickedRef.current = true;
      handlePinClick(pins[0]);
    }
  }, [pins, authChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pollAllSensors = () => {
      pins.forEach(pin =>
        (pin.sensors ?? []).forEach(s => fetchSensorData(s.deviceId, true))
      );
    };
    const interval = setInterval(pollAllSensors, SENSOR_POLL_MS);
    return () => clearInterval(interval);
  }, [pins]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch("/power_lines.geojson")
      .then(res => res.json())
      .then(data => {
        const processed = {
          ...data,
          features: data.features.map((feature, idx) => ({
            ...feature,
            properties: { ...feature.properties, id: `power_line_${idx}` },
          })),
        };
        setLines(processed);
      });
  }, []);

  // ── handlers ───────────────────────────────────────────────────────────────

  const handleDeletePin = (pinId) => {
    const removed = pins.find((p) => p.id === pinId);
    setPins((pins) => pins.filter((p) => p.id !== pinId));
    if (removed?.nodeId) persistNode('DELETE', `/${removed.nodeId}`);
    if (selectedPin && selectedPin.id === pinId) {
      setSelectedPin(null); setDashboardOpen(false);
      setSensorData(null); setSensorError(null);
    }
  };

  const resetRegisterForm = () => {
    setRegisterOpen(false);
    setRegisterDeviceId('');
    setRegisterType('tilt');
    setRegisterLabel('');
    setRegisterError(null);
    setRegisterBusy(false);
  };

  const handleAddPin = (latlng) => {
    if (dashboardOpen) return;
    // The sensor registry behind this dialog is a superuser-only fetch, so for
    // other tiers the dialog would open with an empty dropdown.
    if (!isSuperuser) return;
    setPendingLatLng(latlng);
    setSelectedSensorId('');
    setAddMode('new');
    setAreaInput('');
    setSelectedNodeId('');
    resetRegisterForm();
    setAddPinDialogOpen(true);
  };

  // Declares a device in the sensor registry so it can be attached to a node
  // before ingestion has ever seen it. Writes nothing to TTN and provisions
  // nothing on the device — it only tells SAMST the device exists.
  const handleRegisterDevice = () => {
    // Same gate as every other write. The form is not rendered for lower tiers,
    // so this is the backstop rather than the check that matters.
    if (!isSuperuser) return;
    const deviceId = registerDeviceId.trim();
    if (!deviceId || registerBusy) return;

    setRegisterBusy(true);
    setRegisterError(null);
    apiFetch(`${API_BASE}/api/sensors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        device_id: deviceId,
        sensor_type: registerType,
        // Omitted rather than sent as '' so the row carries no label attribute
        // when the operator did not give one.
        ...(registerLabel.trim() ? { label: registerLabel.trim() } : {}),
      }),
    })
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `HTTP error: ${res.status}`);
        return body;
      })
      .then(profile => {
        // Add it to the dropdown immediately and select it, so the operator can
        // finish the job they opened this dialog for in one pass. The refetch
        // below then reconciles with what the table actually holds.
        setAvailableSensors(prev => {
          const others = prev.filter(s => s.PK !== profile.PK);
          return [...others, profile];
        });
        setSelectedSensorId(profile.PK);
        resetRegisterForm();
        fetchAvailableSensors();
      })
      .catch(err => {
        console.error("Sensor register error:", err);
        setRegisterError(err.message);
        setRegisterBusy(false);
      });
  };

  const handleConfirmAddPin = () => {
    if (!selectedSensorId) return;
    const sensor = availableSensors.find(s => s.PK === selectedSensorId);
    const newSensor = { deviceId: selectedSensorId, sensor_type: sensor?.sensor_type ?? '' };

    if (addMode === 'new') {
      if (!pendingLatLng) return;
      const id = Date.now();
      const area = areaInput.trim();
      const newPin = {
        id,
        nodeId: `node-${id}`,
        label: `Node ${pins.length + 1}`,
        position: [pendingLatLng.lat, pendingLatLng.lng],
        // Optional free-text area name. Left undefined rather than '' so the
        // attribute is simply absent for nodes the operator did not name.
        // The area describes the node's location, so it is set once when the
        // node is created and not touched when sensors are added to it later.
        area: area || undefined,
        sensors: [newSensor],
      };
      setPins(prev => [...prev, newPin]);
      persistNode('POST', '', {
        nodeId: newPin.nodeId,
        label: newPin.label,
        position: newPin.position,
        area: newPin.area,
        sensors: newPin.sensors,
      });
    } else {
      if (!selectedNodeId) return;
      const node = pins.find(p => p.nodeId === selectedNodeId);
      if (!node) return;
      // Refuse here as well as disabling the button (#199). The optimistic
      // setPins below is what makes a rejected write so confusing: the sensor
      // is drawn on the map, the dialog closes, and the 400 arrives afterwards
      // as a banner that names neither sensor nor node. Not starting is the
      // only version of this with nothing to undo.
      if (isDuplicateSelection({
        addMode, pins, nodeId: selectedNodeId, deviceId: selectedSensorId,
      })) return;
      const sensors = [...(node.sensors ?? []), newSensor];
      setPins(prev => prev.map(p =>
        p.nodeId === selectedNodeId ? { ...p, sensors } : p
      ));
      // Only the sensor list changed. Sending the whole node back would let a
      // stale label or position from this tab overwrite another user's edit.
      persistNode('PUT', `/${selectedNodeId}`, { sensors });
    }
    setAddPinDialogOpen(false);
    setPendingLatLng(null);
    setAreaInput('');
  };

  // Open a node's panel on one of its sensor tabs.
  const openNodeSensor = (pin, sensorIndex = 0) => {
    setSelectedPin(pin);
    setSelectedSensorIndex(sensorIndex);
    setDashboardOpen(true);
    setSensorError(null);

    const deviceId = pin.sensors?.[sensorIndex]?.deviceId;
    activeDeviceIdRef.current = deviceId ?? null;
    if (!deviceId) {
      setSensorData(null); setSensorLoading(false);
      return;
    }
    if (sensorCacheRef.current[deviceId]) {
      setSensorData(sensorCacheRef.current[deviceId]);
      setSensorLoading(false);
    } else {
      setSensorData(null); setSensorLoading(true);
    }
    fetchSensorData(deviceId);
  };

  const handlePinClick = (pin) => openNodeSensor(pin, 0);

  // Switch to another sensor in the node already shown in the panel.
  const handleSensorTabClick = (index) => {
    setSelectedSensorIndex(index);
    setSensorError(null);
    const deviceId = selectedNode?.sensors?.[index]?.deviceId;
    activeDeviceIdRef.current = deviceId ?? null;
    if (!deviceId) { setSensorData(null); setSensorLoading(false); return; }
    if (sensorCacheRef.current[deviceId]) {
      setSensorData(sensorCacheRef.current[deviceId]);
      setSensorLoading(false);
    } else {
      setSensorData(null); setSensorLoading(true);
    }
    fetchSensorData(deviceId);
  };

  // Dropping to 'guest' re-runs the tier effect above, which swaps the pins back
  // to a generated set and clears the panel — so nothing to reset here beyond
  // the tier itself.
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error("Sign out error:", err);
    }
    setAccountMenuAnchor(null);
    setLoginModalOpen(false);
    setAuthTier('guest');
    setUsername(null);
  };

  const handleCloseDashboard = () => {
    setDashboardOpen(false); setSelectedPin(null);
    setSelectedSensorIndex(0); activeDeviceIdRef.current = null;
    setSensorData(null); setSensorError(null);
    setExpandedPanel(false);
  };

  // ── search: live suggestions ─────────────────────────────────────────────────

  const SENSOR_TYPE_MAP = { tilt: 'Tilt sensors', pir: 'Motion sensors', temperature: 'Temperature sensors', weather: 'Weather stations' };

  // Build the suggestion list for the current query. Sensor-name and sensor-type
  // matches are synchronous; the location match geocodes via Nominatim.
  const computeSuggestions = async (value) => {
    const seq = ++searchSeqRef.current;
    const query = value.trim().toLowerCase();
    const suggestions = [];

    // 1. Match sensor names, and node labels — a node matches if either its
    //    own label or one of its sensors' device IDs matches the query.
    pins.forEach(p => {
      const labelMatch = p.label?.toLowerCase().includes(query);
      (p.sensors ?? []).forEach((s, i) => {
        if (!labelMatch && !s.deviceId?.toLowerCase().includes(query)) return;
        suggestions.push({
          type: 'sensor',
          label: s.deviceId,
          sublabel: `${p.label} · ${s.sensor_type} · ${p.position[0].toFixed(4)}, ${p.position[1].toFixed(4)}`,
          pin: p,
          sensorIndex: i,
        });
      });
    });

    // 1b. Match area names, so "Podgorica" finds every node in that area.
    // A node already listed by its device name is not pushed twice.
    pins
      .filter(p => p.area?.toLowerCase().includes(query))
      .forEach(p => {
        if (suggestions.some(s => s.pin?.id === p.id)) return;
        suggestions.push({
          type: 'sensor',
          label: p.label,
          sublabel: `📍 ${p.area} · ${(p.sensors ?? []).map(s => s.sensor_type).join(', ')}`,
          pin: p,
          sensorIndex: 0,
        });
      });

    // 2. Match sensor types
    Object.entries(SENSOR_TYPE_MAP).forEach(([type, label]) => {
      if (label.toLowerCase().includes(query) || type.toLowerCase().includes(query)) {
        const count = pins.reduce(
          (n, p) => n + (p.sensors ?? []).filter(s => s.sensor_type === type).length, 0);
        if (count > 0) suggestions.push({
          type: 'filter',
          label,
          sublabel: `${count} sensor${count > 1 ? 's' : ''} on map`,
          sensorType: type,
        });
      }
    });

    // Show the synchronous matches immediately so the dropdown never feels laggy.
    if (seq === searchSeqRef.current) setSearchSuggestions([...suggestions]);

    // 3. Geocode location via Nominatim (free, no API key required)
    try {
      setSearchLoading(true);
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=3&countrycodes=me`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const places = await res.json();
      (Array.isArray(places) ? places : []).forEach(place => {
        const lat = parseFloat(place.lat);
        const lng = parseFloat(place.lon);
        // Find pins within ~10km of this location
        const nearby = pins.filter(p => {
          if (!p.position) return false;
          const dlat = p.position[0] - lat;
          const dlng = p.position[1] - lng;
          return Math.sqrt(dlat * dlat + dlng * dlng) < 0.09; // ~10km
        });
        suggestions.push({
          type: 'location',
          label: place.display_name.split(',').slice(0, 2).join(','),
          sublabel: nearby.length > 0
            ? `${nearby.length} sensor${nearby.length > 1 ? 's' : ''} nearby`
            : 'No sensors in this area',
          position: [lat, lng],
          nearby,
        });
      });
    } catch (e) {
      // Nominatim failed silently, suggestions still show sensor matches
    } finally {
      // Only apply if this is still the latest query (avoids race between
      // overlapping debounced requests).
      if (seq === searchSeqRef.current) {
        setSearchSuggestions([...suggestions]);
        setSearchLoading(false);
      }
    }
  };

  // Debounced onChange handler — runs as the user types.
  const handleSearchChange = (value) => {
    setSearchText(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (value.trim().length < 2) {
      searchSeqRef.current++; // invalidate any in-flight geocode
      setSearchSuggestions([]);
      setSearchLoading(false);
      return;
    }
    searchDebounceRef.current = setTimeout(() => computeSuggestions(value), 400);
  };

  // Fit the map roughly around a set of pins.
  const centerOnPins = (list) => {
    if (!list || list.length === 0) return;
    const avgLat = list.reduce((s, p) => s + p.position[0], 0) / list.length;
    const avgLng = list.reduce((s, p) => s + p.position[1], 0) / list.length;
    setMapCenter([avgLat, avgLng]);
  };

  const clearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchSeqRef.current++;
    setSearchText('');
    setSearchSuggestions([]);
    setSearchLoading(false);
  };

  // Handle a click on a suggestion row.
  const handleSuggestionSelect = (s) => {
    if (s.type === 'sensor') {
      // Jump to the pin and open the panel on that specific sensor's tab
      setMapCenter([...s.pin.position]);
      openNodeSensor(s.pin, s.sensorIndex ?? 0);
      setSearchText(s.label);
      setSearchSuggestions([]);
      setSearchActive(false);
    } else if (s.type === 'filter') {
      // Filter the map to show only sensors of this type
      setSensorTypeFilter(s.sensorType);
      centerOnPins(pins.filter(p => (p.sensors ?? []).some(x => x.sensor_type === s.sensorType)));
      setSearchText(s.label);
      setSearchSuggestions([]);
      setSearchActive(false);
    } else if (s.type === 'location') {
      // Shift the map view to the location; if sensors are nearby, list them
      // so the user can click one to open its panel.
      setMapCenter([...s.position]);
      setSearchText(s.label);
      if (s.nearby && s.nearby.length > 0) {
        setSearchSuggestions(s.nearby.flatMap(p =>
          (p.sensors ?? []).map((sensor, i) => ({
            type: 'sensor',
            label: sensor.deviceId,
            sublabel: `${p.label} · ${sensor.sensor_type} · ${p.position[0].toFixed(4)}, ${p.position[1].toFixed(4)}`,
            pin: p,
            sensorIndex: i,
          }))
        ));
      } else {
        setSearchSuggestions([]);
        setSearchActive(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // If suggestions are open, Enter selects the first one.
    if (searchSuggestions.length > 0) {
      handleSuggestionSelect(searchSuggestions[0]);
      return;
    }
    let parts = searchText.split(',').map(s => s.trim());
    if (parts.length === 2) {
      let lat = parseFloat(parts[0]); let lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const id = Date.now();
        const newPin = {
          id,
          nodeId: `node-${id}`,
          label: `Node ${pins.length + 1}`,
          position: [lat, lng],
          sensors: [],
        };
        setPins(prev => [...prev, newPin]);
        // Same as the dialog: a node created from the search bar is still a
        // node, so it has to outlive the refresh too.
        persistNode('POST', '', {
          nodeId: newPin.nodeId,
          label: newPin.label,
          position: newPin.position,
          sensors: newPin.sensors,
        });
        setMapCenter([lat, lng]); clearSearch(); setSearchActive(false); return;
      }
    }
    alert("Please enter coordinates in 'latitude, longitude' format (e.g. 42.4413, 19.2629)");
  };

  const selectedHealth = activeSensor ? healthMap[activeSensor.deviceId] : null;

  // The assign-sensor dialog's own validation (#199). Derived rather than held
  // in state so it tracks both dropdowns without an effect: the operator can
  // change either one and the message appears and clears as they do.
  const duplicateSensorSelected = isDuplicateSelection({
    addMode, pins, nodeId: selectedNodeId, deviceId: selectedSensorId,
  });
  const duplicateSensorNodeLabel =
    findNode(pins, selectedNodeId)?.label ?? selectedNodeId;

  // One banner slot, so the states are ranked rather than stacked: a map that
  // could not load its nodes is a worse problem than missing health, which is
  // worse than a config the backend had to guess at.
  const banner =
    nodesError === 'load'
      ? { error: true, title: t("nodesLoadError"), hint: t("nodesLoadErrorHint") }
    : nodesError === 'save'
      ? { error: true, title: t("nodesSaveError"), hint: t("nodesSaveErrorHint") }
    : healthError
      ? { error: true, title: t("healthDataUnavailable"), hint: t("healthRetrying") }
    : healthConfigStatus === "degraded"
      ? { error: false, title: t("configDegradedBanner"), hint: t("configDegradedHint") }
    : null;

  // Mobile chrome stacks vertically down the top of the screen: search bar (12)
  // → category chips (64) → optional type-filter chip (108) → optional weather
  // banner → anomalies. Each optional row pushes the ones below it, so the
  // offsets are derived once here instead of being repeated at each call site.
  const mobileWeatherTop = 108 + (sensorTypeFilter ? 44 : 0);

  // A snapshot whose sources could not be reached says nothing about the
  // weather, so it must not be shown as a confirmed all-clear — hiding the
  // banner is indistinguishable from "checked, nothing wrong" (#57, same
  // reasoning as the hollow unknown pin in #33). is_weather_event === null
  // means neither source answered; a single 'unavailable' status means the
  // boolean is real but was decided on partial information. Snapshots written
  // before #57 carry no *_status fields and so keep the old behaviour.
  const weatherUnknown = !!weather && weather.is_weather_event !== true && (
    weather.is_weather_event === null ||
    weather.alerts_status === "unavailable" ||
    weather.conditions_status === "unavailable"
  );

  const weatherBanner = weather?.is_weather_event
    ? {
        bg: weather.highest_alert_color === 'red' ? '#fef2f2' :
            weather.highest_alert_color === 'orange' ? '#fff7ed' : '#fefce8',
        border: weather.highest_alert_color === 'red' ? '#fecaca' :
                weather.highest_alert_color === 'orange' ? '#fed7aa' : '#fde68a',
        fg: weather.highest_alert_color === 'red' ? '#991b1b' :
            weather.highest_alert_color === 'orange' ? '#9a3412' : '#854d0e',
        icon: weather.highest_alert_color === 'red' ? '🔴' :
              weather.highest_alert_color === 'orange' ? '🟠' : '🟡',
        text: weather.summary,
      }
    : weatherUnknown
      ? {
          // Muted grey, not a warning colour: this is an absence of
          // information, not a detected hazard.
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '⚪',
          text: weather.is_weather_event === null
            ? t("weatherStatusUnavailable")
            : t("weatherStatusPartial"),
        }
      : null;

  // Gap between stacked mobile chrome rows, so each reads as a separate object
  // rather than one touching stack.
  const MOBILE_STACK_GAP = 8;

  // The corridor-segment summary sits this far above the bottom of the map,
  // clear of the 48 px floating sidebar trigger at bottom: 16. Shared with the
  // height cap below, so the gap it leaves and the height it is allowed can
  // never drift apart.
  const SEGMENT_SUMMARY_BOTTOM = 72;

  const weatherBannerRef = useRef(null);
  const fireBannerRef = useRef(null);
  const ndviBannerRef = useRef(null);

  // ── top-right controls vs. the chip row (#104) ─────────────────────────────

  // The chip row and the language + account cluster share the y=24 band. The row
  // used to have no right bound on desktop, so it simply grew until it covered
  // the cluster — first in Montenegrin, where the category labels are about 17%
  // longer, and eventually in English too as categories were added. Bounding it
  // against the cluster's measured width fixes it for every language and any
  // number of chips at once, and leaves `left` alone so the row stays aligned
  // with the weather and fire banners.
  const controlClusterRef = useRef(null);
  // Fallback ≈ flag button + "Logout" + gap, i.e. the English guest-tier width.
  // Only ever used for the first paint and in jsdom.
  const controlClusterWidth = useMeasuredWidth(controlClusterRef, 140);
  const desktopChipRowRight =
    DESKTOP_CONTROLS_RIGHT + controlClusterWidth + DESKTOP_CHROME_GAP;

  // Which ends of the row are clipped, so the fade is only drawn where there is
  // actually something past the edge. Hiding the scrollbar is fine while nothing
  // is cut off; once something is, the row needs to say so.
  const chipRowRef = useRef(null);
  const [chipRowClipped, setChipRowClipped] = useState({ start: false, end: false });
  useEffect(() => {
    const el = chipRowRef.current;
    if (!el) return undefined;
    const measure = () => setChipRowClipped(prev => {
      const start = el.scrollLeft > 1;
      const end = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
      return (prev.start === start && prev.end === end) ? prev : { start, end };
    });
    measure();
    el.addEventListener('scroll', measure, { passive: true });

    // A vertical wheel over a horizontal-only scroller does nothing in Chrome or
    // Firefox, so without this a desktop mouse could not reach the far chip —
    // which on this row is the fire-detections toggle, i.e. exactly the control
    // #104 was reported against. Trackpads send deltaX themselves and shift
    // +wheel is already horizontal; both are left alone. At either end the event
    // is not consumed, so the page still gets it. Registered here rather than as
    // onWheel because React attaches wheel listeners passively, where
    // preventDefault is ignored.
    const onWheel = (e) => {
      if (e.deltaX !== 0 || e.shiftKey) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const next = Math.max(0, Math.min(max, el.scrollLeft + e.deltaY));
      if (next === el.scrollLeft) return;
      e.preventDefault();
      el.scrollLeft = next;
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    const detach = () => {
      el.removeEventListener('scroll', measure);
      el.removeEventListener('wheel', onWheel);
    };
    if (typeof ResizeObserver === 'undefined') return detach;
    // The children are observed as well as the container. Now that the row is
    // bounded its own box stops changing when its contents do, so watching only
    // the container would miss the cases that matter — a longer label after a
    // language switch, the fire toggle's live count gaining a digit, and web
    // fonts arriving after the first measure.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach(child => ro.observe(child));
    return () => { detach(); ro.disconnect(); };
  }, [isMobile, language, isSuperuser, fires?.status, expandedPanel, desktopChipRowRight]); // eslint-disable-line react-hooks/exhaustive-deps

  // A mask rather than an overlay element: the row is the scroll container, so a
  // child pinned to its edge would scroll away with the chips, and there is no
  // wrapper to hang one on without disturbing the shared `left` alignment.
  const chipRowMask = (() => {
    if (!chipRowClipped.start && !chipRowClipped.end) return 'none';
    const stops = [chipRowClipped.start ? 'transparent 0px' : '#000 0px'];
    if (chipRowClipped.start) stops.push(`#000 ${CHIP_ROW_FADE}px`);
    if (chipRowClipped.end) stops.push(`#000 calc(100% - ${CHIP_ROW_FADE}px)`);
    stops.push(chipRowClipped.end ? 'transparent 100%' : '#000 100%');
    return `linear-gradient(to right, ${stops.join(', ')})`;
  })();

  // The banner's expandable detail (#92). One group per distinct warning, not
  // one per stored alert — the feed sends a separate entry per warned region,
  // so the same warning arrives several times over (see weatherAlerts.js).
  // No groups means nothing to expand: alerts is null when the feed could not
  // be reached, and empty when is_weather_event was decided by severe
  // conditions rather than by a warning. Neither has per-alert detail to show,
  // so the banner stays a plain one-liner with no expander.
  const weatherAlertGroups = groupAlerts(weather?.alerts);
  const weatherDetailAvailable = weatherAlertGroups.length > 0;
  const weatherDetailShown = weatherDetailAvailable && weatherDetailOpen;

  // Reset once there is nothing to expand, so a warning that lapses and returns
  // on a later poll does not bring the banner back already open.
  useEffect(() => {
    if (!weatherDetailAvailable) setWeatherDetailOpen(false);
  }, [weatherDetailAvailable]);

  // Measured, never assumed — see useMeasuredHeight. Frozen while the detail is
  // expanded: the banner then deliberately overlays what is below it at zIndex
  // 2050 rather than pushing it down, so the rows below must keep stepping from
  // its collapsed height.
  const weatherBannerHeight = useMeasuredHeight(weatherBannerRef, 40, {
    freeze: weatherDetailShown,
    watch: weatherBanner,
  });

  // ── fire detections (#93) ──────────────────────────────────────────────────

  // One box for the whole fire-detection state, rather than an alert banner plus
  // a separate legend. Whatever it says, it always says both how confident the
  // detection is and when the area was last checked — the two facts that stop a
  // thermal anomaly being read as a confirmed fire, and stop an empty result
  // being read as an all-clear.
  //
  // It shows when either:
  //   * something is within alert_radius_km of a node — regardless of the layer
  //     toggle, because that is the actionable case; or
  //   * the operator has turned the layer on and therefore asked the question.
  const fireNearest = fires?.nearest_detection ?? null;
  // 'partial' is kept apart from 'unavailable': some sources answered, so the
  // detections shown are real and only the coverage is incomplete. Saying
  // "FIRMS could not be reached" there would be wrong in both directions.
  const firePartial = fires?.status === 'partial';
  const fireUnknown = !!fires && fires.status !== 'ok' && !firePartial;

  // The poller asks FIRMS for a rectangle, and FIRMS answers with a hard edge.
  // When detections reach that edge the cluster continues into ground nobody
  // asked about, so `detection_count` is a floor and must not be printed as
  // though it were a total (#100). Strictly `=== true`: the field is null when
  // the box could not be measured against, and an unknown must not silently
  // become "not clipped".
  const fireClipped = fires?.edge_clipped === true;
  // Appended rather than folded into each banner string, so the clipped case
  // does not need a second copy of every sentence in two languages.
  const withClipped = (text) => (fireClipped ? `${text} ${t('fireBannerClipped')}` : text);

  const fireBanner = !fires ? null
    : fireNearest
      ? {
          bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', icon: '🔥',
          // Never "fire detected N km from X": FIRMS reports a thermal anomaly,
          // and industrial heat and sun glint both produce them.
          //
          // The age is here for the same reason the confidence is: this banner
          // may be the only thing an operator reads, and a distance quoted
          // without a time reads as "right now" when the acquisition could be
          // most of a day old — the satellite has probably not passed since.
          text: t('fireBannerNear', {
            km: fireNearest.nearest_node.distance_km.toFixed(1),
            node: fireNearest.nearest_node.label,
            confidence: fireNearest.confidence
              ? t(`fireConfidence_${fireNearest.confidence}`)
              : typeof fireNearest.confidence_pct === 'number'
                ? t('fireConfidencePct', { n: fireNearest.confidence_pct })
                : t('fireConfidenceUnknown'),
            age: formatDetectionAge(fireNearest.acquired_at, t) ?? t('fireAgeUnknown'),
          }),
        }
    : !firesVisible ? null
    : fireUnknown
      ? {
          // Grey, like the unknown weather banner: an absence of information,
          // not a detected hazard.
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '⚪',
          text: fires.reason
            ? t('fireBannerUnavailableReason', { reason: fires.reason })
            : t('fireBannerUnavailable'),
        }
    : fires.stale
      ? {
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '⚪',
          text: t('fireBannerStale', { n: fires.check_age_minutes }),
        }
    : firePartial
      ? {
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '⚪',
          text: withClipped(t('fireBannerPartial', {
            failed: (fires.sources_failed ?? []).length,
            total: (fires.sources_ok ?? []).length + (fires.sources_failed ?? []).length,
            n: fires.detection_count ?? 0,
          })),
        }
    : fires.detection_count > 0
      ? {
          bg: '#fff7ed', border: '#fed7aa', fg: '#9a3412', icon: '🔥',
          // The only banner that states a bare count as the answer, so it is
          // the one a clipped result misleads most.
          text: withClipped(t('fireBannerSomeFar', {
            n: fires.detection_count,
            hours: fires.lookback_hours,
            km: fires.alert_radius_km,
          })),
        }
      : {
          // The most dangerous state to get wrong. "No detections" is not "no
          // fire": a polar-orbiting satellite passes a few times a day and FIRMS
          // publishes no overpass time for an area it flagged nothing in, so the
          // only honest claim is about what was checked, not about what is there.
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '⚪',
          text: t('fireBannerNone', { hours: fires.lookback_hours }),
        };

  // Not frozen: this banner has no expanded state, so its rendered height is
  // always the height the row below it should step over.
  const fireBannerHeight = useMeasuredHeight(fireBannerRef, 40, { watch: fireBanner });

  // ── Sentinel-2 vegetation (#103) ───────────────────────────────────────────

  // One line for the whole vegetation state. Whatever it says, it always carries
  // the date the satellite actually looked — an NDVI figure with no date reads
  // as "now", when the scene behind it can legitimately be three weeks old.
  //
  // The ranking below is deliberate and is not the order of severity. Anything
  // that means "we did not measure" outranks every measured class, because a
  // green line drawn from an absent reading is the one failure this feature
  // could introduce that the operator cannot detect.
  const ndviPartial = typeof satellite?.valid_fraction === 'number'
    && satellite.valid_fraction < NDVI_PARTIAL_VIEW_BELOW;

  // Shown next to every measured reading. Not a footnote: an area mean over 40%
  // of the corridor and one over all of it are different claims.
  const ndviCoverage = ndviPartial
    ? t('ndviPartialView', { pct: Math.round(satellite.valid_fraction * 100) })
    : '';

  // Two states, and deliberately not four.
  //
  // A sudden drop is a hazard and is coloured like one. Everything else is a
  // measurement, shown in neutral grey with no verdict attached.
  //
  // The absolute classes the Lambda computes — normal, moderate_stress,
  // high_stress — are not rendered as such. Their thresholds (0.40 / 0.25) are
  // fixed numbers that know nothing about the month, and Podgorica is
  // Mediterranean: vegetation dries hard every summer and NDVI sits in the 0.3s
  // through August by nature. The first live reading was 0.383 on 28 August 2026
  // from a 99.96% cloud-free scene — an accurate measurement that a coloured
  // badge would have rendered as amber "vegetation stress" with nothing having
  // happened on the ground. A badge amber for a third of every year teaches
  // people to stop reading it, and it is then ignored in the year it matters,
  // which is #107's ~335 emails a day reached by a different route.
  //
  // So the number is stated and left to the reader: value, date, and "area
  // average" in the line itself rather than as a footnote, because that is the
  // one fact that stops 0.38 being read as a claim about any particular span.
  //
  // The delta has none of that problem. Seasonal drying is gradual — hundredths a
  // week — and never approaches the 0.15 fall that classifies a sudden_drop,
  // whereas a fire or a clearing does it in one step. The change is a signal; the
  // level is a season.
  //
  // Nothing is dropped from the pipeline either way. This data is cumulative and
  // cannot be backfilled: with two summers of it, "0.38 this August against 0.51
  // last August" becomes a real statement about the corridor, and the absolute
  // reading becomes worth classifying against a seasonal baseline instead of a
  // constant. That is when to revisit the three unused strings. See #103.
  const ndviReading = typeof satellite?.ndvi_mean === 'number'
    ? {
        value: satellite.ndvi_mean.toFixed(2),
        date: formatSceneDate(satellite.scene_date) ?? t('ndviDateUnknown'),
      }
    : null;

  const ndviBanner = !satellite ? null
    // The delta is required, not assumed. The Lambda cannot classify a drop
    // without one, but a hand-edited or partially written row could arrive
    // claiming otherwise, and "NDVI fell 0.00 in null days" would be worse than
    // showing nothing.
    : satellite.status === 'ok'
      && !satellite.stale
      && satellite.stress_level === 'sudden_drop'
      && ndviReading
      && typeof satellite.ndvi_delta === 'number'
      && typeof satellite.delta_days === 'number'
      ? {
          bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', icon: '🔴',
          // Never "possible fire". NDVI falls for fire, felling, drought,
          // harvest and grazing alike, and the index cannot tell them apart —
          // naming one of them here would put a cause on screen that the
          // measurement does not support.
          text: t('ndviBannerSuddenDrop', {
            drop: Math.abs(satellite.ndvi_delta).toFixed(2),
            days: satellite.delta_days,
            date: ndviReading.date,
            value: ndviReading.value,
          }) + ndviCoverage,
        }
    // A measurement exists. Stated plainly, in the same muted grey the weather
    // and fire banners use for "no information" — because that is what this is:
    // a fact, not a finding.
    //
    // Shown when stale too, with the age appended. The alternative is hiding the
    // line when the weekly run stops landing, which removes the only place the
    // failure is visible at the moment it starts mattering. A number labelled as
    // three weeks old is honest; the same number unlabelled would not be.
    : ndviReading
      ? {
          bg: '#f8fafc', border: '#e2e8f0', fg: '#475569', icon: '🛰',
          text: t('ndviBannerReading', ndviReading)
            + (satellite.stale && typeof satellite.check_age_days === 'number'
                ? t('ndviBannerReadingStale', { n: satellite.check_age_days })
                : '')
            + ndviCoverage,
        }
    // No measurement: never ran, Copernicus unreachable, or every pass clouded
    // out. Nothing is rendered. An operator cannot act on any of the three, and
    // a line saying "vegetation status unknown" would occupy the stack
    // permanently while the corridor is observed perfectly well by every other
    // means on this map.
      : null;

  const ndviBannerHeight = useMeasuredHeight(ndviBannerRef, 40, { watch: ndviBanner });

  // Desktop chrome stacks under the search bar in fixed 44px steps: weather
  // banner at 70, fire box below it, vegetation below that. Vegetation is last
  // because it is the slowest-moving of the three — a weekly area average, next
  // to a 30-minute forecast and a 15-minute detection feed.
  const desktopFireTop = weatherBanner ? 114 : 70;
  const desktopNdviTop = 70 + (weatherBanner ? 44 : 0) + (fireBanner ? 44 : 0);

  // Mobile: each row steps over the measured height of the one above it. #93
  // stepped by a fixed 40px here, the same assumption #92 had to abandon. It
  // matters more for this banner, not less: its text carries distance, confidence
  // and acquisition age in one line, so on a phone it wraps to two or three and a
  // fixed step would drop the anomalies card straight on top of it.
  const mobileFireTop = mobileWeatherTop
    + (weatherBanner ? weatherBannerHeight + MOBILE_STACK_GAP : 0);
  const mobileNdviTop = mobileFireTop
    + (fireBanner ? fireBannerHeight + MOBILE_STACK_GAP : 0);
  const mobileAnomaliesTop = mobileNdviTop
    + (ndviBanner ? ndviBannerHeight + MOBILE_STACK_GAP : 0);

  // How much room is left underneath the top stack, for the one overlay that
  // grows from the other edge — the corridor-segment summary, a Leaflet control
  // in the map's bottom-left corner.
  //
  // It cannot be a fraction of the viewport. The stack above it is four
  // conditional banners plus an anomalies panel that expands when tapped, so on
  // a phone "half the screen" is sometimes free and sometimes already occupied;
  // a flat 45vh fitted on one layout and slid under Detected Anomalies on
  // another. Measured, never assumed — the same rule the banner offsets follow.
  //
  // A hidden anomalies panel keeps its last measured height rather than
  // reporting 0 (see useMeasuredHeight), so the summary stays conservatively
  // short while the panel is covered instead of growing and then jumping back.
  const anomaliesBoxRef = useRef(null);
  const anomaliesBoxHeight = useMeasuredHeight(anomaliesBoxRef, 140);
  const segmentSummaryMaxHeight = isMobile
    ? `calc(100dvh - ${mobileAnomaliesTop + anomaliesBoxHeight + MOBILE_STACK_GAP + SEGMENT_SUMMARY_BOTTOM}px)`
    : 'none';

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{
      position: 'relative', width: '100vw',
      // 100dvh, not 100vh. On iOS Safari `vh` is the LARGE viewport — it counts
      // the strip under the bottom browser toolbar as visible. Everything Leaflet
      // anchors to the bottom of the map then sits below what the user can see:
      // the corridor-segment summary was cut off mid-sentence on a phone, and no
      // amount of bottom margin fixes it because the margin is measured from an
      // edge that is itself off screen. `dvh` tracks the toolbar as it hides and
      // shows. `@supports` carries the old behaviour for anything that does not
      // know `dvh` — note an ARRAY here would mean responsive breakpoints in MUI,
      // not a CSS fallback, and would put 100vh back on exactly the phones this
      // is fixing.
      height: '100dvh',
      '@supports not (height: 100dvh)': { height: '100vh' },
      overflow: 'hidden',
      // Leaflet renders its controls inside the map at a low z-index, so the
      // zoom buttons sat underneath the search bar and chip row. They are moved
      // to the bottom-right corner (see ZoomControl below); this lifts them
      // clear of the fixed simulated-data banner whenever that is showing.
      '& .leaflet-bottom.leaflet-right': {
        marginBottom: simulated ? '40px' : '8px',
      },
    }}>

      {/* Search Bar. On mobile it spans the full width and carries the language
          and login controls at its right end, Google-Maps style — the left rail
          is hidden there, so nothing overlaps its left edge any more.

          The suggestions dropdown is nested inside this container and sets no
          z-index of its own, so it cannot be raised on its own — this container
          is the stacking context and has to win (#84). At rest it stays at 1400
          alongside the chip row and weather banner, which it does not overlap.
          While the dropdown is open it goes above 2000, because on mobile the
          dropdown falls straight down the screen through everything stacked
          under the bar: chip row (1400), type-filter chip (1399), weather
          banner (1400) and — the reason 1600 is not enough — the anomalies card
          (2000), which starts around y=108 while the dropdown spans roughly
          y=60 to y=400. Raising the bar is preferred over hiding the anomalies
          card: nothing disappears and reflows, and the card's display rule
          already juggles three unrelated conditions. 2100 stays well below the
          login overlay (9999), so modals are unaffected.

          Desktop is untouched in practice: the bar spans x=84..484 there while
          every element this outranks starts at DESKTOP_CHROME_LEFT (650) or is
          pinned to the right edge, so nothing overlaps it either way. */}
      <Box sx={{
        position: 'absolute',
        zIndex: searchSuggestions.length > 0 ? 2100 : 1400,
        top: isMobile ? 12 : 24,
        left: isMobile ? 8 : 84,
        right: isMobile ? 8 : 'auto',
        width: isMobile ? 'auto' : (searchActive ? 400 : 280),
        transition: 'width 0.25s cubic-bezier(.4,1.3,.8,1)',
      }}>
        <Paper elevation={6} component="form"
          sx={{ display: 'flex', alignItems: 'center', width: '100%', borderRadius: searchSuggestions.length > 0 ? '24px 24px 0 0' : 8, px: 1, py: 0.5, boxShadow: 3, bgcolor: '#fff', cursor: 'text' }}
          onClick={() => setSearchActive(true)} onSubmit={handleSearch}
        >
          <InputBase sx={{ ml: 1, flex: 1, fontSize: 18 }} inputRef={searchInputRef}
            placeholder={t("searchPlaceholder")} value={searchText}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => setSearchActive(true)}
            onBlur={() => { setSearchActive(false); setTimeout(() => setSearchSuggestions([]), 150); }}
            inputProps={{ 'aria-label': 'search' }}
          />
          {searchText && (
            <IconButton aria-label="clear search" onMouseDown={e => e.preventDefault()}
              onClick={clearSearch} sx={{ p: '4px', color: '#9ca3af' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton type="submit" sx={{ p: '6px', ml: 0.5, color: '#1976d2', borderRadius: '50%' }}>
            <SearchIcon />
          </IconButton>

          {/* On mobile the language and account controls live inside the search
              bar instead of in their own top-right cluster, which previously
              floated over the map and forced the bar to give up 120px of width.
              stopPropagation keeps these taps off the Paper's focus handler. */}
          {isMobile && (
            <>
              <Box sx={{ width: '1px', height: 24, bgcolor: '#e5e7eb', mx: 0.5, flexShrink: 0 }} />
              <IconButton
                aria-label="toggle language"
                onClick={(e) => { e.stopPropagation(); setLanguage(language === 'en' ? 'me' : 'en'); }}
                sx={{ p: '4px', fontSize: 16, lineHeight: 1, flexShrink: 0 }}
              >
                {language === 'en' ? '🇬🇧' : '🇲🇪'}
              </IconButton>
              {/* The avatar opens the account menu below rather than acting
                  directly. It used to sign the user out on a single unlabelled
                  tap, with the S/U letter as the only clue to who was signed in
                  and at what tier (#60). */}
              <IconButton
                aria-label={t("accountMenu")}
                aria-haspopup="true"
                aria-expanded={Boolean(accountMenuAnchor)}
                onClick={(e) => { e.stopPropagation(); setAccountMenuAnchor(e.currentTarget); }}
                sx={{ p: 0, ml: 0.25, flexShrink: 0 }}
              >
                <Box sx={{
                  width: 30, height: 30, borderRadius: '50%',
                  bgcolor: authTier === 'superuser' ? '#10b981'
                         : authTier === 'user' ? '#6b7280'
                         : '#1976d2',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                }}>
                  {authTier === 'superuser' ? 'S'
                    : authTier === 'user' ? 'U'
                    : <PersonOutlineIcon sx={{ fontSize: 18 }} />}
                </Box>
              </IconButton>

              {/* Account menu. Renders through a portal, so the Paper's focus
                  handler never sees these taps and no stopPropagation is needed
                  on the items. Sign-out now takes a deliberate second tap on a
                  labelled control, and the header answers "am I a superuser?",
                  which had no UI answer at all before. */}
              <Menu
                anchorEl={accountMenuAnchor}
                open={Boolean(accountMenuAnchor)}
                onClose={() => setAccountMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 190, mt: 0.5 } } }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography sx={{
                    fontSize: 13, fontWeight: 600, color: '#111827',
                    // A login id can be a long email; wrap rather than widen the
                    // menu past the phone's screen.
                    wordBreak: 'break-all',
                  }}>
                    {username ?? t("accountNotSignedIn")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 0.25 }}>
                    {authTier === 'superuser' ? t("tierSuperuser")
                      : authTier === 'user' ? t("tierUser")
                      : t("tierGuest")}
                  </Typography>
                </Box>
                <Divider />
                {authTier !== 'guest' ? (
                  <MenuItem
                    onClick={() => { setAccountMenuAnchor(null); handleLogout(); }}
                    sx={{ fontSize: 13 }}
                  >
                    {t("logout")}
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={() => { setAccountMenuAnchor(null); setLoginModalOpen(true); }}
                    sx={{ fontSize: 13 }}
                  >
                    {t("login")}
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Paper>

        {/* Live suggestions dropdown */}
        {searchSuggestions.length > 0 && (
          <Paper elevation={6} onMouseDown={e => e.preventDefault()}
            sx={{ borderRadius: '0 0 16px 16px', maxHeight: 340, overflowY: 'auto', boxShadow: 3, bgcolor: '#fff', borderTop: '1px solid #eee' }}>
            {searchSuggestions.map((s, i) => (
              <Box key={`${s.type}-${s.label}-${i}`}
                onClick={() => handleSuggestionSelect(s)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: '#f5f7fa' }, borderTop: i === 0 ? 'none' : '1px solid #f2f2f2' }}
              >
                <Box sx={{ color: s.type === 'location' ? '#ef4444' : s.type === 'filter' ? '#8b5cf6' : '#1976d2', display: 'flex' }}>
                  {s.type === 'location' ? <PlaceOutlinedIcon /> : s.type === 'filter' ? <FilterAltOutlinedIcon /> : <SensorsIcon />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.sublabel}</Typography>
                </Box>
              </Box>
            ))}
            {searchLoading && (
              <Typography sx={{ px: 2, py: 1, fontSize: 12, color: '#9ca3af' }}>Searching locations…</Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Active sensor-type filter chip. This filter is set from a search
          suggestion rather than the category row, so the chip is the only way to
          clear it — it has to stay on screen, below the chip row on mobile. */}
      {sensorTypeFilter && (
        <Box sx={{
          position: 'absolute', zIndex: 1399,
          top: isMobile ? 108 : 28,
          left: isMobile ? 8 : 500,
          display: expandedPanel ? 'none' : 'flex', alignItems: 'center',
        }}>
          <Paper elevation={3} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.5, pr: 0.5, py: 0.25, borderRadius: 20, bgcolor: '#ede9fe' }}>
            <FilterAltOutlinedIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6d28d9' }}>
              {SENSOR_TYPE_MAP[sensorTypeFilter]} only
            </Typography>
            <IconButton aria-label="clear filter" size="small" onClick={() => setSensorTypeFilter(null)} sx={{ color: '#6d28d9' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Paper>
        </Box>
      )}

      {/* Language toggle + login/logout, desktop only. On mobile the same two
          controls are rendered inside the search bar above.

          Above the chip row's 1400 rather than tied with it. Bounded, the row
          should never reach this far; the tie mattered because DOM order broke
          it in the worst possible direction — the chips are declared later, so
          they painted on top and the language button became unclickable, which
          leaves no way back from a language you cannot read except a reload.
          Ranked, the same failure would hide chips under the controls instead.
          Insurance for the narrow-window case the width floor still allows, not
          a substitute for the bound (#104). */}
      <Box ref={controlClusterRef} sx={{
        position: 'absolute', zIndex: 1450,
        top: 24,
        right: DESKTOP_CONTROLS_RIGHT,
        display: (expandedPanel || isMobile) ? 'none' : 'flex',
        flexDirection: 'row', alignItems: 'center',
        gap: 1,
      }}>
        <Button onClick={() => setLanguage(language === 'en' ? 'me' : 'en')}
          sx={{ minWidth: 0, px: isMobile ? 1 : 1.5, py: 0.5, borderRadius: 2, bgcolor: '#fff', boxShadow: 2, fontSize: isMobile ? 16 : 20, lineHeight: 1, '&:hover': { bgcolor: '#f5f5f5' } }}>
          {language === 'en' ? '🇬🇧' : '🇲🇪'}
        </Button>
        {authTier !== 'guest' ? (
          <Button
            onClick={handleLogout}
            sx={{ minWidth: 0, px: isMobile ? 1 : 1.5, py: 0.5, borderRadius: 2,
                  bgcolor: '#fff', boxShadow: 2, fontSize: isMobile ? 11 : 13,
                  '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            {t("logout")}
          </Button>
        ) : (
          <Button
            onClick={() => setLoginModalOpen(true)}
            sx={{ minWidth: 0, px: isMobile ? 1 : 1.5, py: 0.5, borderRadius: 2,
                  bgcolor: '#1976d2', color: '#fff', boxShadow: 2,
                  fontSize: isMobile ? 11 : 13, '&:hover': { bgcolor: '#1565c0' } }}
          >
            {t("login")}
          </Button>
        )}
      </Box>

      {/* Weather banner — shown while an alert or severe conditions are active,
          and in a muted grey variant when the weather sources could not be
          reached, so an unknown state is never presented as an all-clear.
          On desktop it centres below the search bar, where there is free space.
          On mobile it slots into the top stack beneath the chip row (and beneath
          the type-filter chip when that is showing) rather than sitting at a
          fixed 70px, which would land on top of the chips. It also wraps there
          instead of using nowrap, since a summary can easily exceed 390px.

          The one-line summary stays the collapsed state (#92); the per-alert
          detail is revealed on hover on desktop and on tap on mobile, and is
          rendered inside this same box so it hangs off the banner rather than
          being positioned against the viewport a second time. Both gestures are
          wired, not one or the other: a touch device fires no mouseenter, and a
          desktop pointer should not have to click something that looks like a
          status line. */}
      {weatherBanner && (
        <Box
          ref={weatherBannerRef}
          role={weatherDetailAvailable ? 'button' : undefined}
          tabIndex={weatherDetailAvailable ? 0 : undefined}
          aria-expanded={weatherDetailAvailable ? weatherDetailShown : undefined}
          aria-label={weatherDetailAvailable
            ? (weatherDetailShown ? t("weatherAlertHideDetail") : t("weatherAlertShowDetail"))
            : undefined}
          onMouseEnter={weatherDetailAvailable && !isMobile
            ? () => setWeatherDetailOpen(true) : undefined}
          onMouseLeave={weatherDetailAvailable && !isMobile
            ? () => setWeatherDetailOpen(false) : undefined}
          onClick={weatherDetailAvailable
            ? () => setWeatherDetailOpen(open => !open) : undefined}
          onKeyDown={weatherDetailAvailable ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setWeatherDetailOpen(open => !open);
            } else if (e.key === 'Escape') {
              setWeatherDetailOpen(false);
            }
          } : undefined}
          sx={{
            position: 'absolute',
            top: isMobile ? mobileWeatherTop : 70,
            left: isMobile ? 8 : DESKTOP_CHROME_LEFT,
            right: isMobile ? 8 : 'auto',
            // Open, the banner has to outrank whatever it now hangs over: on
            // mobile the anomalies card (2000) sits 40px below it. Same ceiling
            // as #84 — this stays under the search bar's 2100, so an open
            // suggestions list is never covered by a passing hover.
            zIndex: weatherDetailShown ? 2050 : 1400,
            display: expandedPanel ? 'none' : 'flex',
            flexDirection: 'column',
            bgcolor: weatherBanner.bg,
            border: `1px solid ${weatherBanner.border}`,
            borderRadius: 2,
            px: isMobile ? 1.25 : 2,
            py: 0.75,
            boxShadow: 2,
            maxWidth: isMobile ? 'none' : 480,
            // Only the summary line is nowrap on desktop; the detail below sets
            // its own wrapping, or a region list would run off the edge.
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            cursor: weatherDetailAvailable ? 'pointer' : 'default',
            outlineOffset: 2,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              color: weatherBanner.fg,
            }}>
              {weatherBanner.icon}{' '}{weatherBanner.text}
            </Typography>
            {/* Sized to the summary's own line box so the collapsed banner is
                exactly as tall as it was before — the mobile stack below it is
                laid out against a fixed 40px. */}
            {weatherDetailAvailable && (
              weatherDetailShown
                ? <ExpandLessIcon sx={{ fontSize: 16, color: weatherBanner.fg, ml: 'auto', flexShrink: 0 }} />
                : <ExpandMoreIcon sx={{ fontSize: 16, color: weatherBanner.fg, ml: 'auto', flexShrink: 0 }} />
            )}
          </Box>

          {weatherDetailShown && (
            // A tap inside the detail must not collapse it — on a phone the list
            // scrolls, and reaching the last warning means touching it. Closing
            // stays with the header row, whose chevron has flipped.
            <Box onClick={(e) => e.stopPropagation()} sx={{
              mt: 0.75, pt: 0.75,
              borderTop: `1px solid ${weatherBanner.border}`,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              // A phone showing nine regional warnings would otherwise run off
              // the bottom of the screen with no way to reach the last one.
              maxHeight: isMobile ? 'calc(100dvh - 260px)' : 340,
              overflowY: 'auto',
              minWidth: isMobile ? 0 : 300,
            }}>
              <WeatherAlertDetail
                groups={weatherAlertGroups}
                t={t}
                compact={isMobile}
                color={weatherBanner.fg}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Fire detection status (#93). Sits directly below the weather banner —
          the two compose: Meteoalarm gives regional fire *risk*, FIRMS gives
          actual thermal anomalies with coordinates, and seeing both at once says
          more than either alone. Same placement rules as the weather banner, one
          step further down. */}
      {fireBanner && (
        <Box ref={fireBannerRef} sx={{
          position: 'absolute',
          top: isMobile ? mobileFireTop : desktopFireTop,
          left: isMobile ? 8 : DESKTOP_CHROME_LEFT,
          right: isMobile ? 8 : 'auto',
          zIndex: 1400,
          display: expandedPanel ? 'none' : 'flex',
          bgcolor: fireBanner.bg,
          border: `1px solid ${fireBanner.border}`,
          borderRadius: 2,
          px: isMobile ? 1.25 : 2,
          py: 0.75,
          alignItems: 'center',
          gap: 1,
          boxShadow: 2,
          maxWidth: isMobile ? 'none' : 480,
        }}>
          <Typography sx={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: fireBanner.fg }}>
            {fireBanner.icon}{' '}{fireBanner.text}
          </Typography>
        </Box>
      )}

      {/* Sentinel-2 vegetation status (#103). Third step of the same stack, and
          last of the three on purpose: it is a weekly area average, the
          slowest-moving thing on this screen, and it should not sit above a
          thermal anomaly 2 km from a tower.

          It composes with the two above rather than duplicating them. Meteoalarm
          gives regional fire *risk*, FIRMS gives thermal anomalies happening now,
          and this gives the state of the fuel between the towers over weeks —
          the only one of the three that can show a corridor drying out before
          anything ignites.

          Superuser-only like every other /api/ consumer: `satellite` stays null
          for lower tiers, so this renders nothing at all. */}
      {ndviBanner && (
        <Box ref={ndviBannerRef} sx={{
          position: 'absolute',
          top: isMobile ? mobileNdviTop : desktopNdviTop,
          left: isMobile ? 8 : DESKTOP_CHROME_LEFT,
          right: isMobile ? 8 : 'auto',
          zIndex: 1400,
          display: expandedPanel ? 'none' : 'flex',
          bgcolor: ndviBanner.bg,
          border: `1px solid ${ndviBanner.border}`,
          borderRadius: 2,
          px: isMobile ? 1.25 : 2,
          py: 0.75,
          alignItems: 'center',
          gap: 1,
          boxShadow: 2,
          maxWidth: isMobile ? 'none' : 480,
        }}>
          <Typography sx={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: ndviBanner.fg }}>
            {ndviBanner.icon}{' '}{ndviBanner.text}
          </Typography>
          {/* "Corridor NDVI 0.38 · 12 Sep · area average" is the line most
              likely to be read as a statement about a span — which is #103 and
              #146 both. The help says what it is over and what it is not. */}
          <HelpTip topic="corridorNdvi" t={t} sx={{ ml: 'auto', flexShrink: 0 }} />
        </Box>
      )}

      {/* Category buttons. On mobile they sit directly under the search bar as a
          horizontally scrollable chip row — the container already scrolled, it
          was simply parked off screen at left: 650 and hidden. Short labels keep
          all three on screen; the row scrolls once a type is added or the text
          runs long in another language.

          Both edges are pinned on either layout now. `right: 'auto'` on desktop
          meant the row's width was its content width, so it never overflowed and
          `overflowX` never did anything — it just grew rightward until it landed
          on the language and account controls, which is #104. The bound is the
          measured cluster plus a gap, so it holds in any language and at any
          number of chips, and `left` is untouched so the row stays aligned with
          the two banners below it. */}
      <Box ref={chipRowRef} sx={{
        borderRadius: 8, position: 'absolute', zIndex: 1400,
        top: isMobile ? 64 : 24,
        left: isMobile ? 8 : DESKTOP_CHROME_LEFT,
        right: isMobile ? 8 : desktopChipRowRight,
        minWidth: isMobile ? 0 : CHIP_ROW_MIN_WIDTH,
        display: expandedPanel ? 'none' : 'flex',
        gap: 1, overflowX: "auto", pb: 1, pr: isMobile ? 1 : 0,
        '&::-webkit-scrollbar': { display: 'none' },
        // Firefox has no ::-webkit-scrollbar; without this it would show a
        // scrollbar on desktop for the first time, under the fade.
        scrollbarWidth: 'none',
        maskImage: chipRowMask,
        WebkitMaskImage: chipRowMask,
      }}>
        {/* LAYER TOGGLES — deliberately BEFORE the sensor-type filters (#146).

            These two add overlays; the blue chips to their right hide pins. They
            used to sit at the far end of the row, which was wrong for a reason
            that only shows up over time: the filter set grows with every sensor
            type added, the toggle set does not. The row is already narrower than
            its content — the short category labels run to roughly 1030px full
            length, ~660px shortened, against about 590px of space at a 1440px
            window — so whatever is last gets clipped, and what was clipped was
            the only control that turns the layer on at all. A fade is the right
            affordance for "more filters that way". It is not enough for "the
            feature is over here", which is how #146 shipped unusable.

            Fixed-size group first, growable set second, so the controls that
            cannot be reached any other way are always on screen.

            On mobile the label drops to the icon and the count. Two full labels
            at the head of a ~390px row would push every filter off screen, which
            is the same defect pointed the other way. The icon carries the
            category, `title` keeps the full name on hover, and `aria-label`
            keeps it for a screen reader, to which a bare "2/2" says nothing. */}
        {isSuperuser && fires && (
          <Button
            variant="outlined"
            startIcon={<LocalFireDepartmentIcon sx={{ fontSize: 18 }} />}
            aria-pressed={firesVisible}
            aria-label={t('catFires')}
            // The full name, and nothing more. This attribute used to carry the
            // explanation of the "+" as well, which meant the one thing a
            // reader needed in order not to misread the count was reachable by
            // hover only — i.e. not on a phone at all. That moved to the help
            // control beside this chip (#171); `title` is back to being a
            // label, which is all a native title can honestly be.
            title={t('catFires')}
            onClick={() => setFiresVisible(v => !v)}
            sx={{
              borderRadius: 20, textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 500,
              px: isMobile ? 1.25 : 2, fontSize: isMobile ? 13 : 15, flexShrink: 0,
              bgcolor: firesVisible ? '#ea580c' : '#fff',
              color: firesVisible ? '#fff' : '#9a3412',
              borderColor: firesVisible ? '#ea580c' : '#fed7aa',
              '&:hover': {
                bgcolor: firesVisible ? '#c2410c' : '#fff7ed',
                borderColor: '#ea580c',
              },
            }}
          >
            {/* The count is part of the control on purpose: it carries the state
                that the layer being off would otherwise hide. "?" rather than
                "0" whenever nothing was checked, so an unchecked area never
                reads as an area with nothing in it — and "n+" wherever the count
                is real but the coverage behind it is not complete: a partial
                poll, and a poll whose detections reach the edge of the box that
                was asked about (#100). Computed once so the mobile and desktop
                forms cannot drift apart. */}
            {(() => {
              const n = fires.status === 'ok'
                  ? `${fires.detection_count ?? 0}${fireClipped ? '+' : ''}`
                : fires.status === 'partial' ? `${fires.detection_count ?? 0}+`
                : '?';
              return isMobile ? n : `${t('catFiresShort')} (${n})`;
            })()}
          </Button>
        )}
        {/* Outside the Button, not inside it: a button nested in a button is
            invalid markup, and a tap meant for the help would also toggle the
            layer. `fireCountBounded` is appended only while the count on screen
            actually is a floor — it is a claim about this count, not about the
            feature — and the lookback comes from the response rather than being
            restated here, so a `?hours=` override cannot make the help wrong. */}
        {isSuperuser && fires && (
          <HelpTip
            topic="firesCount"
            t={t}
            vars={{ hours: fires.lookback_hours ?? 24 }}
            extraKeys={fireClipped ? ['fireCountBounded'] : []}
            sx={{ alignSelf: 'center', flexShrink: 0 }}
          />
        )}

        {isSuperuser && segmentNdvi && (
          <Button
            variant="outlined"
            startIcon={<GrassIcon sx={{ fontSize: 18 }} />}
            aria-pressed={segmentsVisible}
            aria-label={t('catSegmentNdvi')}
            // A label, like the fires chip above. The sentence this attribute
            // used to carry — what a per-segment NDVI value actually is — is now
            // the opening of the segmentNdvi help topic, so it is reachable on a
            // phone instead of on hover only (#171).
            title={t('catSegmentNdvi')}
            onClick={() => setSegmentsVisible(v => !v)}
            sx={{
              borderRadius: 20, textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 500,
              px: isMobile ? 1.25 : 2, fontSize: isMobile ? 13 : 15, flexShrink: 0,
              bgcolor: segmentsVisible ? '#31a354' : '#fff',
              color: segmentsVisible ? '#fff' : '#166534',
              borderColor: segmentsVisible ? '#31a354' : '#bbf7d0',
              '&:hover': {
                bgcolor: segmentsVisible ? '#15803d' : '#f0fdf4',
                borderColor: '#31a354',
              },
            }}
          >
            {/* Segments WITH A MEASURED VALUE out of all segments, not a plain
                total, and "?" whenever the values could not be read. "2/2" and
                "0/2" are different situations — the second is a corridor nobody
                can currently see — and a bare "2" would hide both. */}
            {(() => {
              const n = segmentNdvi.status === 'ok'
                  ? `${segmentNdvi.segments.filter(s => typeof s.ndvi_mean === 'number').length}/${segmentNdvi.segments.length}`
                : segmentNdvi.status === 'empty' ? '0'
                : '?';
              return isMobile ? n : `${t('catSegmentNdviShort')} (${n})`;
            })()}
          </Button>
        )}
        {isSuperuser && segmentNdvi && (
          <HelpTip topic="segmentNdvi" t={t} sx={{ alignSelf: 'center', flexShrink: 0 }} />
        )}

        {/* One divider for the whole group, not one per toggle — it separates
            overlays from filters, so it belongs at the boundary. Absent when
            neither toggle rendered, so a non-superuser never sees a rule with
            nothing on one side of it. */}
        {isSuperuser && (fires || segmentNdvi) && (
          <Box sx={{ width: '1px', bgcolor: '#e0e0e0', flexShrink: 0, my: 0.5, mx: 0.5 }} />
        )}

        {categories.map((cat) => (
          <Button key={cat.label} variant="outlined" color="primary" startIcon={cat.icon}
            sx={{
              borderRadius: 20, textTransform: "none", whiteSpace: "nowrap", fontWeight: 500,
              px: isMobile ? 1.5 : 2, fontSize: isMobile ? 13 : 15, flexShrink: 0,
              bgcolor: activeTypeFilter === cat.sensor_type ? '#1976d2' : '#fff',
              color: activeTypeFilter === cat.sensor_type ? '#fff' : 'inherit',
              borderColor: activeTypeFilter === cat.sensor_type ? '#1976d2' : '#e0e0e0',
              "&:hover": { bgcolor: activeTypeFilter === cat.sensor_type ? '#1565c0' : '#f5f5f5', borderColor: "#1976d2" }
            }}
            onClick={() => setActiveTypeFilter(prev =>
              prev === cat.sensor_type ? null : cat.sensor_type
            )}
            // Short labels on desktop too, with the full name on hover. The full
            // set at fontSize 15 runs to roughly 1030px, while between
            // DESKTOP_CHROME_LEFT and the control cluster the row has about 590px
            // at a 1440px window — so it was permanently clipped, not
            // occasionally. Bounding it in #104 only made that visible. Short
            // forms bring the set to roughly 660px, which fits a normal desktop
            // and leaves the fade for the cases that still do not. The icon
            // carries the category, and the mobile row has read this way since
            // #44.
            title={cat.label}
          >{cat.short}</Button>
        ))}

      </Box>

      {/* Active filter dismissal chip, desktop only. On mobile the category row
          is now on screen and shows the active type highlighted, and tapping it
          again clears the filter — so this chip would just be clutter there. */}
      {activeTypeFilter && !isMobile && (
        <Box sx={{
          position: 'absolute',
          top: 72,
          left: 650,
          zIndex: 1400, display: 'flex', alignItems: 'center'
        }}>
          <Chip
            label={`Showing: ${activeTypeFilter} sensors`}
            onDelete={() => setActiveTypeFilter(null)}
            size="small"
            sx={{ bgcolor: '#fff', boxShadow: 1 }}
          />
        </Box>
      )}

      {/* Sidebar. On mobile the 56px collapsed rail is removed entirely: it sat
          at zIndex 1500 over the search bar and clipped its left edge, and it
          cost width the map needs. The trigger moves to a floating button near
          the thumb (below); when open, the panel is full-screen as before, so
          Sidebar itself is unchanged. overflow: hidden makes width 0 truly hide
          the contents rather than letting them spill over the map. */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, height: '100%',
        width: sidebarOpen ? (isMobile ? '100vw' : 320) : (isMobile ? 0 : 56),
        bgcolor: '#f5f5f5', zIndex: 1500,
        boxShadow: (isMobile && !sidebarOpen) ? 0 : 3,
        transition: 'width 0.3s', display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', overflow: 'hidden',
      }}>
        {/* The sidebar trigger and the help launcher, both OUTSIDE the <Fade>
            below — that is what keeps the hamburger alive in the 56px collapsed
            rail, and the help control inherits the same property: always on
            screen, one click, no discovery step. Putting help inside the panel
            would mean it is only found by someone who already opened the sidebar
            looking for something.

            The two layouts are structurally different and are written out
            separately rather than forced into one element. Desktop stacks
            hamburger-then-help down a 56px rail. Mobile puts close-left and
            help-right on a single row — and that row needs an explicit
            width: '100%', because the container is flexDirection: 'column' with
            alignItems: 'flex-start' and a row that does not stretch would put
            "top right" wherever the content happens to end. */}
        {(!isMobile || sidebarOpen) && (
          isMobile ? (
            <Box sx={{
              width: '100%', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <IconButton sx={{ m: 1 }} onClick={() => setSidebarOpen(false)}>
                <CloseIcon />
              </IconButton>
              <HelpLauncher t={t} language={language} sx={{ m: 1 }} />
            </Box>
          ) : (
            <>
              <IconButton sx={{ m: 1 }} onClick={() => setSidebarOpen(!sidebarOpen)}>
                <MenuIcon />
              </IconButton>
              {/* m: 1 on both, so each control occupies exactly one 56px row
                  (8 + 40 + 8) and the height budget below is two of them. */}
              <HelpLauncher t={t} language={language} sx={{ m: 1 }} />
            </>
          )
        )}
        {/* Height budget for the scrolling panel: one 56px control row on
            mobile (close and help share it), two stacked on desktop. Hardcoded
            before #172 as a single 56, which would now overflow by a row. */}
        <Fade in={sidebarOpen} unmountOnExit>
          <Box sx={{ width: '100%', height: `calc(100% - ${isMobile ? 56 : 112}px)`, overflow: 'hidden' }}>
            <Sidebar open={sidebarOpen} healthMap={healthMap} healthError={healthError}
              apiBaseUrl={API_BASE} appVersion={APP_VERSION} isSuperuser={isSuperuser}
              t={t} language={language} />
          </Box>
        </Fade>
      </Box>

      {sidebarOpen && !isMobile && (
        <Box onClick={() => setSidebarOpen(false)}
          sx={{ position: 'fixed', left: 320, top: 0, width: 'calc(100vw - 320px)', height: '100vh', bgcolor: 'rgba(0,0,0,0.07)', zIndex: 1450 }}
        />
      )}

      {/* Mobile: dim everything behind the sidebar so it reads as a modal panel,
          and let a tap outside it close the sidebar. Sits under the sidebar's
          own zIndex of 1500. */}
      {sidebarOpen && isMobile && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            position: 'fixed', inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1490,
          }}
        />
      )}

      {/* Mobile: the sidebar trigger as a floating button within thumb reach,
          since the left rail is gone. It lifts above the simulated-data banner
          when that is showing so the two never overlap, and stands down while a
          full-screen panel is up. */}
      {isMobile && !sidebarOpen && (
        <IconButton
          aria-label="open menu"
          onClick={() => setSidebarOpen(true)}
          sx={{
            position: 'fixed',
            left: 12,
            bottom: simulated ? 48 : 16,
            zIndex: 1480,
            width: 48, height: 48,
            bgcolor: '#fff', boxShadow: 3,
            display: (expandedPanel || (panelFullScreen && dashboardOpen)) ? 'none' : 'flex',
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Map */}
      {/* zoomControl={false} + an explicit bottom-right ZoomControl: the default
          top-left position put the +/- buttons under the search bar on mobile
          and under the sidebar rail on desktop. */}
      <MapContainer center={[42.4413, 19.2629]} zoom={13} zoomControl={false}
        maxZoom={SATELLITE_MAX_ZOOM} style={{ height: "100%", width: "100%" }}>
        <ZoomControl position="bottomright" />
        {/* maxZoom on both: the TileLayer value is what Leaflet derives the map's
            own limit from, but setting it here too means the +/- control and every
            programmatic flyTo share one explicit ceiling.

            The imagery itself is old. The tile over the pilot area is byte-identical
            across every Esri Wayback release from 2021-04-08 to 2026-08-05, so it
            was last reflown in or before April 2021 and no newer version exists to
            fetch. Treat what is on screen as site context of unknown age, never as
            the current state of a site. */}
        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={SATELLITE_MAX_ZOOM} attribution='Tiles © Esri' />

        {/* Credited only while a Sentinel-derived reading is actually shown —
            see CopernicusAttribution on why this is not on the TileLayer. */}
        <CopernicusAttribution active={!!satellite && typeof satellite.ndvi_mean === 'number'} />

        {/* Per-segment corridor NDVI, served as WMS tiles by GeoServer (#146).
            Mounted after the basemap so it composites over it, and it needs no
            z-index bookkeeping to stay under the power lines and the sensor
            pins: Leaflet puts tile layers in the tilePane, which sits beneath
            both the overlayPane and the markerPane. That ordering is the right
            one — this is context around the network, not part of it. */}
        {segmentsVisible && isSuperuser && (
          <SegmentNdviLayer
            baseUrl={API_BASE}
            summary={segmentNdvi}
            maxZoom={SATELLITE_MAX_ZOOM}
            isMobile={isMobile}
            // The summary sits in the map's bottom-left corner, which on desktop
            // is underneath the sidebar rail (absolute, left: 0, height: 100%,
            // zIndex 1500). Pass the rail's current width so the box clears it
            // whether the sidebar is collapsed or open. Zero on mobile, where the
            // rail has no width and the floating trigger is cleared with mb.
            chromeLeft={isMobile ? 0 : (sidebarOpen ? 320 : 56)}
            maxHeight={segmentSummaryMaxHeight}
            bottomOffset={isMobile ? SEGMENT_SUMMARY_BOTTOM : 0}
            nodeLabels={nodeLabelsById}
            t={t}
          />
        )}

        {lines && <GeoJSON data={lines}
          style={feature => { const fid = feature.properties?.id; return { color: fid === selectedLineId ? "red" : "orange", weight: fid === selectedLineId ? 5 : 2, opacity: 0.8 }; }}
          interactive={false}
        />}
        {lines && <GeoJSON data={lines}
          style={{ color: "orange", weight: 15, opacity: 0, fillOpacity: 0 }}
          onEachFeature={(feature, layer) => { layer.on({ click: (e) => { setSelectedLineId(feature.properties.id); L.DomEvent.stopPropagation(e); } }); }}
          interactive={true}
        />}

        <FlyTo position={mapCenter} />

        {/* FIRMS thermal anomalies (#93). Mounted BEFORE the node markers so the
            sensor pins stay on top: this is context around the network, not part
            of it, and a fire cell must never cover the pin whose status the
            operator came to read. */}
        {firesVisible && fires?.detections?.length > 0 && (
          <FireDetectionsLayer
            detections={fires.detections}
            alertRadiusKm={fires.alert_radius_km ?? 10}
            nodesStatus={fires.nodes_status ?? 'ok'}
            isTouch={isMobileDevice}
            t={t}
          />
        )}

        {filteredPins.map(pin => {
          const health = worstHealth(pin, healthMap);
          // Real health wins. Generated pins carry their own status, so the map
          // still reads as live for the tiers that never call /api/. Only when
          // neither exists do we say "unknown" — never assume "ok", so a real
          // device missing from a failed/empty health response cannot show
          // false green.
          const status = health?.status ?? pin.status ?? "unknown";
          const unknownSensors = health?.unknownSensors ?? [];
          return (
            <Marker key={pin.id} position={pin.position} icon={makePinIcon(status)} draggable={true}
              eventHandlers={{
                click: (e) => { e.originalEvent.stopPropagation(); handlePinClick(pin); },
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  setPins(pins => pins.map(p => p.id === pin.id ? { ...p, position: [lat, lng] } : p));
                  persistNode('PUT', `/${pin.nodeId}`, { position: [lat, lng] });
                }
              }}
            >
              {/* A tooltip is a mouse idiom — it opens on hover, and there is no
                  hover on a touch screen. There the first tap only revealed the
                  tooltip and a tap on the tooltip fell through to the map, which
                  read it as "add a sensor here" (#61). On touch the panel *is*
                  the detail view, so one tap opens it and no tooltip is drawn.
                  Where a tooltip is drawn it is interactive: a click that lands
                  on it reaches the marker's own handler (Leaflet makes the
                  bound layer an event parent of its tooltip) instead of passing
                  through to the map, so a hybrid mouse-and-touch device cannot
                  reach the add-sensor dialog by tapping a tooltip either. */}
              {!isMobileDevice && (
              <Tooltip direction="top" offset={[0, -32]} opacity={1} interactive={true}>
                <div style={{ minWidth: 180, fontFamily: "Inter, system-ui, sans-serif" }}>
                  {pin.area && (
                    <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>📍 {pin.area}</div>
                  )}
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: "#111827" }}>{pin.label}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                    {(pin.sensors ?? []).map(s => s.sensor_type).join(', ')}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                    {pin.position[0].toFixed(5)}, {pin.position[1].toFixed(5)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: STATUS_COLOR[status] ?? STATUS_COLOR.unknown, flexShrink: 0 }} />
                    <span style={{ color: STATUS_COLOR[status] ?? STATUS_COLOR.unknown, fontWeight: 600 }}>
                      {statusLabel(status, t)}
                    </span>
                    {health?.battery != null && <span style={{ color: "#6b7280", marginLeft: 4 }}>🔋 {health.battery}%</span>}
                  </div>
                  {/* "Unknown" has two causes: nothing answered for this node at
                      all, or one of several sensors is missing from the health
                      response while a sibling answered. Name the sensors in the
                      second case — otherwise the operator cannot tell which of
                      them is the one we cannot see (#66). */}
                  {status === "unknown" && (
                    <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
                      {healthError
                        ? t("healthDataUnavailable")
                        : unknownSensors.length > 0
                          ? t("noHealthDataForSensors", { list: unknownSensors.map(sensorName).join(', ') })
                          : t("noHealthDataForDevice")}
                    </div>
                  )}
                  {health?.seconds_since_seen != null && (
                    <div
                      style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}
                      title={formatExactTime(health.last_seen) ?? undefined}
                    >
                      {t("lastSeen")} {formatLastSeen(health.seconds_since_seen, t, language)}
                    </div>
                  )}
                </div>
              </Tooltip>
              )}
            </Marker>
          );
        })}

        <CentralMapClickHandler selectedLineId={selectedLineId} setSelectedLineId={setSelectedLineId}
          handleAddPin={handleAddPin} setDashboardOpen={setDashboardOpen}
          setSelectedPin={setSelectedPin} setSensorData={setSensorData} setSensorError={setSensorError}
        />
      </MapContainer>

      {/* Health / config status banner — a monitoring dashboard must say when it
          cannot see, instead of rendering an all-clear it cannot vouch for. */}
      {banner && !expandedPanel && (
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, maxWidth: 'min(90vw, 560px)' }}>
          <Paper elevation={6} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2,
            bgcolor: banner.error ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${banner.error ? '#fecaca' : '#fde68a'}`,
          }}>
            <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{banner.error ? '⚠️' : 'ℹ️'}</Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: banner.error ? '#991b1b' : '#92400e' }}>
                {banner.title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                {banner.hint}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Anomalies panel. On mobile it sits directly under the chip row rather
          than bottom-anchored, where it collided with the fixed simulated-data
          banner. It starts collapsed, so this is a header plus one row; the
          max-height keeps it scrolling inside the viewport once expanded instead
          of running off the bottom. This widget outranks both the sidebar (1500)
          and the sensor panel (1500) on zIndex, so it also stands down whenever
          either of those is covering the screen. */}
      <Box ref={anomaliesBoxRef} sx={{
        position: 'absolute',
        top: isMobile ? mobileAnomaliesTop : 96,
        right: isMobile ? 8 : 32,
        left: isMobile ? 8 : 'auto',
        maxWidth: isMobile ? 'calc(100vw - 16px)' : 'none',
        maxHeight: isMobile ? 'calc(100dvh - 200px)' : 'none',
        overflowY: isMobile ? 'auto' : 'visible',
        bgcolor: 'white', p: isMobile ? 1.25 : 2, borderRadius: 2, boxShadow: 3, zIndex: 2000,
        display: (expandedPanel
          || (panelFullScreen && dashboardOpen)
          || (isMobile && sidebarOpen)) ? 'none' : 'block',
      }}>
        <AnomaliesPanel apiBaseUrl={API_BASE} t={t}
          simulated={simulated} lockedForGuest={authTier === 'guest'}
          compact={isMobile} />
      </Box>

      {/* Node info + sensor data panel */}
      <Slide direction="right" in={dashboardOpen} mountOnEnter unmountOnExit>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, height: '100%',
          width: panelFullScreen ? '100vw' : (expandedPanel ? PANEL_EXPANDED_WIDTH : PANEL_WIDTH),
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
          zIndex: 1500, boxShadow: 3, backgroundColor: 'background.paper',
          p: 2, overflowY: 'auto',
          // Prevent clipping of anything — content only, chevron is outside
          overflow: 'auto',
        }}>
          {/* Header: title + delete + close */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{t("nodeInfo")}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Superuser only: persistNode is a no-op for the other tiers, so
                  their delete would disappear from the map and then come back on
                  the next refresh — worse than not offering it. */}
              {isSuperuser && selectedNode && (
                <Button size="small"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  aria-label={t("deleteNode")} title={t("deleteNode")}
                  onClick={() => setDeleteTarget(selectedNode)}
                  sx={{
                    textTransform: 'none', fontWeight: 500, color: '#b91c1c',
                    px: 1, minWidth: 0, whiteSpace: 'nowrap',
                    '&:hover': { color: '#7f1d1d', bgcolor: '#fef2f2' },
                  }}>
                  {t("delete")}
                </Button>
              )}
              <IconButton size="small" onClick={handleCloseDashboard}
                sx={{ color: '#6b7280', '&:hover': { color: '#111827', bgcolor: '#f3f4f6' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {selectedNode && (
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{selectedNode.label}</Typography>
              <Typography><strong>ID:</strong> {selectedNode.nodeId}</Typography>
              {selectedNode.area && (
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                  📍 {selectedNode.area}
                </Typography>
              )}
              <Typography>
                <strong>Lat:</strong> {selectedNode.position[0].toFixed(5)},&nbsp;
                <strong>Lng:</strong> {selectedNode.position[1].toFixed(5)}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                {activeSensor ? `Sensor: ${activeSensor.deviceId}` : 'No sensors assigned'}
              </Typography>

              {/* Voltage and circuit identity, above everything about the
                  devices (#209). It is what the asset is called in the
                  operator's own documents, and it decides who gets called
                  before anyone climbs. Read from the geojson the map already
                  loaded; nothing absent from the matched feature is invented. */}
              <NodeLineIdentity
                match={selectedLineMatch}
                offCorridor={Boolean(selectedNode.offCorridorReason)}
                t={t}
              />

              {/* Sensor tabs — only when this node holds more than one sensor */}
              {selectedNode.sensors?.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {selectedNode.sensors.map((s, i) => (
                    <Button key={`${s.deviceId}-${i}`} size="small"
                      variant={selectedSensorIndex === i ? 'contained' : 'outlined'}
                      onClick={() => handleSensorTabClick(i)}
                      sx={{ textTransform: 'none' }}
                    >
                      {s.sensor_type || s.deviceId}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Health status row */}
              {selectedHealth && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: STATUS_COLOR[selectedHealth.status], flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: STATUS_COLOR[selectedHealth.status] }}>
                    {statusLabel(selectedHealth.status, t)}
                  </Typography>
                  {/* Beside the dot, on the panel a tap on a pin opens — which
                      is the only route to this row on a touch screen, where the
                      map tooltip is deliberately not drawn (#61). The topic's
                      second paragraph is the grey case: "we cannot tell", never
                      an all-clear (#33, #66). */}
                  <HelpTip topic="deviceStatus" t={t} simulated={simulated} />
                  {selectedHealth.battery != null && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>🔋 {selectedHealth.battery}%</Typography>
                  )}
                  {selectedHealth.seconds_since_seen != null && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', ml: 'auto' }}
                      title={formatExactTime(selectedHealth.last_seen) ?? undefined}
                    >
                      {t("lastSeen")} {formatLastSeen(selectedHealth.seconds_since_seen, t, language)}
                    </Typography>
                  )}
                  {selectedHealth.reason && (
                    <Typography variant="body2" sx={{ color: '#92400e' }}>— {selectedHealth.reason}</Typography>
                  )}
                </Box>
              )}

              {/* Newest usable Sentinel-2 view of the ground around this node
                  (#109). Sits above the sensor data because it is about the
                  site rather than the device, and it fetches only when asked:
                  the first look at each node spends a Copernicus quota. */}
              <NodeSatelliteSnapshot
                nodeId={selectedNode.nodeId}
                apiBaseUrl={API_BASE}
                t={t}
                simulated={!isSuperuser}
              />

              {/* Sentinel-1 radar coherence at this pylon (#118). Directly under
                  the optical snapshot because they answer adjacent questions about
                  the same ground — one shows it, the other says whether it stayed
                  the same between two passes. Stated, never graded: the drop
                  threshold behind it has no calibration yet. */}
              <NodeGroundStability
                nodeId={selectedNode.nodeId}
                apiBaseUrl={API_BASE}
                t={t}
                simulated={!isSuperuser}
              />

              <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>{t("sensorData")}</Typography>
              {sensorLoading && <Typography sx={{ mt: 1, color: 'text.secondary' }}>{t("loadingSensorData")}</Typography>}
              {sensorError && <Typography sx={{ mt: 1, color: 'error.main' }}>{t("failedToLoadSensor")}</Typography>}

              {sensorData && !sensorLoading && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("fieldSensor")}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{sensorData.PK}</Typography>
                  </Box>
                  {/* A registered device that has not reported has no reading
                      timestamp to show. `has_readings === false` is checked
                      explicitly, not for falsiness: generated data carries no
                      such field and must keep rendering as before. */}
                  {sensorData.has_readings === false ? (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t("noDataYetTitle")}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {t("noDataYetBody")}
                      </Typography>
                      {sensorData.registered_at && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                          {t("registeredOn")} {new Date(sensorData.registered_at).toLocaleString()}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', mt: 1, pt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("fieldType")}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{sensorData.sensor_type}</Typography>
                      </Box>
                    </Box>
                  ) : (
                  <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("fieldTimestamp")}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{new Date(sensorData.SK).toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{t("fieldType")}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{sensorData.sensor_type}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}>{t("readings")}</Typography>
                  {/* Flatten one level so nested groups (the station's `raw`
                      bucket) print as ordinary rows instead of a JSON blob.
                      Keys keep their parent prefix, so `raw.rain_gauge` still
                      reads as "raw rain gauge" rather than losing that it is
                      an unverified channel. */}
                  {(() => {
                    const rows = Object.entries(sensorData.payload ?? {})
                      .flatMap(([key, value]) =>
                        value && typeof value === 'object' && !Array.isArray(value)
                          ? Object.entries(value).map(([k, v]) => [`${key}_${k}`, v])
                          : [[key, value]]
                      );
                    // Channels with no known meaning are counted, not listed —
                    // see isPlaceholderChannel. The count still appears, because
                    // dropping them silently would make a reader think the device
                    // sent only what is shown.
                    const named = rows.filter(([key]) => !isPlaceholderChannel(key));
                    const hidden = rows.length - named.length;
                    return (
                      <>
                        {named.map(([key, value]) => (
                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', py: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>
                          </Box>
                        ))}
                        {hidden > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.75, fontStyle: 'italic' }}>
                            {t("hiddenChannels").replace("{n}", hidden)}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                  </>
                  )}
                </Box>
              )}

              {/* Per-sensor visualization panels — driven by the active tab.
                  Guests get a lock in place of the analysis; signed-in
                  non-superusers get the full panel on generated data. */}
              {authTier === 'guest' && ['tilt', 'temperature', 'pir', 'weather', 'power'].includes(activeSensor?.sensor_type) && (
                <LockedPanel
                  message={activeSensor.sensor_type === 'tilt'
                    ? t("lockedDriftTitle")
                    : t("lockedAnalysisTitle")}
                  loginLabel={t("login")}
                  onLogin={() => setLoginModalOpen(true)}
                />
              )}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'tilt' && (
                <Box sx={{ mt: 3 }}>
                  <TowerDriftChart key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} label={activeSensor.deviceId} t={t} simulated={simulated} />
                </Box>
              )}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'temperature' && (
                <Box sx={{ mt: 3 }}>
                  <TemperatureChart key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} label={activeSensor.deviceId} t={t} simulated={simulated} />
                </Box>
              )}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'pir' && (
                <Box sx={{ mt: 3 }}>
                  <PIREventLog key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} label={activeSensor.deviceId} t={t} simulated={simulated} />
                </Box>
              )}
              {/* Weather readings, or the line rating they give at this span
                  (#247). The span direction prefill comes from the nearest-line
                  match above; the operator confirms it in the settings. */}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'weather' && (
                <Box sx={{ mt: 3 }}>
                  <WeatherNodeTabs key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} t={t} simulated={simulated}
                    lineBearingDeg={selectedLineMatch?.bearingDeg ?? null}
                    offCorridor={Boolean(selectedNode.offCorridorReason)} />
                </Box>
              )}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'power' && (
                <Box sx={{ mt: 3 }}>
                  <PowerMeterPanel key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} label={activeSensor.deviceId} t={t} simulated={simulated} />
                </Box>
              )}
              {authTier !== 'guest' && activeSensor?.sensor_type === 'power' && (
                <Box sx={{ mt: 3 }}>
                  <PowerMeterChart key={activeSensor.deviceId} deviceId={activeSensor.deviceId} apiBaseUrl={API_BASE} label={activeSensor.deviceId} t={t} simulated={simulated} />
                </Box>
              )}

              {/* Back to Map — only when expanded */}
              {expandedPanel && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, position: 'sticky', bottom: 16, pr: 2 }}>
                  <Button variant="contained" size="large" onClick={handleCloseDashboard}
                    sx={{ px: 4, py: 1.5, fontSize: 16, boxShadow: 4 }}>
                    {t('backToMap')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Slide>

      {/* Expand/collapse chevron tab — OUTSIDE Slide so it's never clipped.
          Nothing to expand once the panel already fills the screen, and the tab
          would float over the panel at PANEL_WIDTH_PX. */}
      {dashboardOpen && !panelFullScreen && (
        <Box
          onClick={() => setExpandedPanel(p => !p)}
          sx={{
            position: 'fixed',
            top: '50%',
            left: expandedPanel ? 'calc(100vw - 80px)' : PANEL_WIDTH_PX
            ,
            transform: 'translateY(-50%)',
            transition: 'left 0.3s cubic-bezier(.4,0,.2,1)',
            width: 24,
            height: 64,
            bgcolor: '#ffffff',
            boxShadow: 4,
            borderRadius: '0 8px 8px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 2000,
            '&:hover': { bgcolor: '#f3f4f6' },
          }}
        >
          <Typography sx={{ fontSize: 20, color: '#374151', lineHeight: 1, userSelect: 'none', fontWeight: 400 }}>
            {expandedPanel ? '‹' : '›'}
          </Typography>
        </Box>
      )}

      {/* Shadow overlay — tracks the panel width so the two never disagree */}
      {dashboardOpen && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0,
          width: panelFullScreen ? '100vw' : (expandedPanel ? PANEL_EXPANDED_WIDTH : PANEL_WIDTH),
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
          height: '100%', background: 'rgba(0,0,0,0.11)', pointerEvents: 'none', zIndex: 1100
        }} />
      )}

      {/* Search input panel */}
      <Fade in={searchOpen}>
        <Paper elevation={6} sx={{ position: 'absolute', top: 84, left: 84, zIndex: 1400, p: 1, minWidth: 320, display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mx: 1 }} />
          <TextField variant="standard" placeholder="Search location or node..." inputRef={searchInputRef}
            value={searchText} onChange={e => setSearchText(e.target.value)} fullWidth
            InputProps={{ disableUnderline: true, style: { fontSize: 18, paddingLeft: 4, background: 'transparent' } }}
            sx={{ background: 'transparent', minWidth: 200, ml: 1 }}
          />
        </Paper>
      </Fade>

      {/* Add pin dialog */}
      <Dialog open={addPinDialogOpen} onClose={() => setAddPinDialogOpen(false)}>
        <DialogTitle>{t("assignSensor")}</DialogTitle>
        <DialogContent sx={{ minWidth: 320, pt: 2 }}>
          {/* Create a brand-new node here, or attach this sensor to a node that
              already exists rather than cluttering the map with another pin. */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button size="small" variant={addMode === 'new' ? 'contained' : 'outlined'}
              onClick={() => setAddMode('new')}>New node</Button>
            <Button size="small" variant={addMode === 'existing' ? 'contained' : 'outlined'}
              onClick={() => setAddMode('existing')}>Add to existing node</Button>
          </Box>

          {addMode === 'existing' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select node</InputLabel>
              <Select value={selectedNodeId} label="Select node" onChange={e => setSelectedNodeId(e.target.value)}>
                {pins.map(p => (
                  <MenuItem key={p.nodeId} value={p.nodeId}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel>{t("selectSensor")}</InputLabel>
            <Select value={selectedSensorId} label={t("selectSensor")} onChange={e => setSelectedSensorId(e.target.value)}>
              {availableSensors.map(sensor => (
                <MenuItem key={sensor.PK} value={sensor.PK}>
                  {sensor.PK} — {sensor.sensor_type}
                  {/* A device declared by hand that has still not reported. Worth
                      flagging in the list: attaching it to a node is valid, but
                      its panel will have no readings to show yet. */}
                  {sensor.registered === true && !sensor.last_seen && ` — ${t("noDataYetShort")}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Register a device the registry has never seen. Folded away by
              default: the dropdown is the normal path, and this is the escape
              hatch for a device that transmits to TTN but has not arrived in
              SAMST yet — or one whose profile row was never created. */}
          {!registerOpen && (
            <Button
              size="small"
              onClick={() => { setRegisterOpen(true); setRegisterError(null); }}
              sx={{ mt: 1, textTransform: 'none', pl: 0 }}
            >
              {availableSensors.length === 0 ? t("registerDevicePromptEmpty") : t("registerDevicePrompt")}
            </Button>
          )}

          {registerOpen && (
            <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#f9fafb' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("registerDeviceTitle")}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                {t("registerDeviceHint")}
              </Typography>

              <TextField
                label={t("registerDeviceId")}
                placeholder="em320-tilt-sn017"
                value={registerDeviceId}
                onChange={e => { setRegisterDeviceId(e.target.value); setRegisterError(null); }}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>{t("registerDeviceType")}</InputLabel>
                <Select
                  value={registerType}
                  label={t("registerDeviceType")}
                  onChange={e => setRegisterType(e.target.value)}
                >
                  {REGISTERABLE_SENSOR_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={t("registerDeviceLabel")}
                value={registerLabel}
                onChange={e => setRegisterLabel(e.target.value)}
                fullWidth
                size="small"
              />

              {registerError && (
                <Typography variant="body2" sx={{ mt: 1.5, color: 'error.main' }}>
                  {registerError}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRegisterDevice}
                  disabled={!registerDeviceId.trim() || registerBusy}
                >
                  {registerBusy ? t("registering") : t("register")}
                </Button>
                <Button size="small" onClick={resetRegisterForm} disabled={registerBusy}>
                  {t("cancel")}
                </Button>
              </Box>
            </Box>
          )}
          {/* The area names the node's location, so it is only offered when a
              new node is being created — an existing node keeps the area it
              was given. */}
          {addMode === 'new' && (
            <TextField
              label={t("areaName")}
              placeholder={t("areaPlaceholder")}
              value={areaInput}
              onChange={e => setAreaInput(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
          )}

          {/* Said here, in the dialog, rather than left to the 400 (#199). The
              backend still rejects it — it has to, since more than one client
              writes sensors[] — but by the time that answer arrives this dialog
              has closed and the sensor has been drawn on the map, so it can
              only surface as the generic "change was not saved" banner. */}
          {duplicateSensorSelected && (
            <Typography
              role="alert"
              sx={{ mt: 2, color: 'error.main', fontSize: 14 }}
            >
              {t("sensorAlreadyOnNode").replace("{node}", duplicateSensorNodeLabel)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPinDialogOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleConfirmAddPin}
            disabled={!selectedSensorId
              || (addMode === 'existing' && !selectedNodeId)
              || duplicateSensorSelected}>
            {addMode === 'new' ? t("addPin") : 'Add sensor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete-node confirmation. Removing a node also drops its sensor
          assignments, and there is no undo, so it is worth one tap of friction. */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("deleteNodeConfirm")}</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{deleteTarget?.label}</strong>
            {deleteTarget?.area ? ` — 📍 ${deleteTarget.area}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {t("deleteNodeWarning", { n: deleteTarget?.sensors?.length ?? 0 })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("cancel")}</Button>
          <Button variant="contained" color="error"
            onClick={() => { handleDeletePin(deleteTarget.id); setDeleteTarget(null); }}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login modal — Cognito hosted UI components over the map */}
      {loginModalOpen && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 9999,
          bgcolor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3, p: 3,
            minWidth: 360, position: 'relative',
          }}>
            <IconButton
              aria-label="close login"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              onClick={() => setLoginModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Authenticator>
              {() => (
                <AuthSessionSync onResolved={({ tier, username: name }) => {
                  setAuthTier(tier);
                  setUsername(name);
                  setLoginModalOpen(false);
                }} />
              )}
            </Authenticator>
          </Box>
        </Box>
      )}

      {/* Persistent simulated-data banner for every tier below superuser */}
      {simulated && (
        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          zIndex: 2000, bgcolor: 'rgba(30,30,30,0.82)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 2, py: 0.75, px: 2,
        }}>
          <Typography sx={{ fontSize: 11, color: '#e5e7eb' }}>
            {authTier === 'guest' ? t("simulatedBannerGuest") : t("simulatedBannerUser")}
          </Typography>
          {authTier === 'guest' && (
            <Button size="small"
              onClick={() => setLoginModalOpen(true)}
              sx={{ fontSize: 11, py: 0.25, px: 1, color: '#93c5fd',
                    minWidth: 0, textTransform: 'none' }}>
              {t("loginArrow")}
            </Button>
          )}
        </Box>
      )}

    </Box>
  );
}

function App() {
  console.log("2. App component is rendering");
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/database" element={
          <Authenticator>
            {({ signOut, user }) => <DatabasePage signOut={signOut} user={user} />}
          </Authenticator>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
